import React,{useState,useRef,useCallback,useEffect} from 'react';
import {DrawingToolbar,Pitch,PitchFormatRow} from '../components/Match';
import {AchievementBadges,Flag,FlagPicker,HomeAwaySplitCard,IconPickerSelect,MatchTimeTable,NotesThread,PlayerDevelopmentSection,PlayerPhotoUploader,ReorderableBlocks,StatsFilterMenu,useStatsVisibility} from '../components/Shared';
import {api} from '../lib/api';
import {ACHIEVEMENTS,AGE_CATEGORIES,ATT,ORG_STATS_DEFAULT_ORDER,POSITIONS,TEAM_COLORS} from '../lib/constants';
import {calcAge,computeEntitlements,downloadPlayerCSVTemplate,fmtDOB,fmtDate,genCode,isEligible,loadBlockOrder,mkSlots,modalBackEffect,parseJerseyNumber,parsePlayerCSV,saveBlockOrder,today} from '../lib/utils';

export function PlayerBaseView({org, allPlayers, teams, groups, orgAttendance, onAddPlayer, onBulkAddPlayers, onUpdatePlayer, onDeletePlayer, ac, AS, isMobile, mode, onOpenNotes, playerAchievements, onLoadPlayerAchievements, onAwardAchievement, token, coachId, onPlayerPhotoUpdated, onParentCodeUpdated}){
  const entitlements=computeEntitlements(org);
  const [expandedCodeId,setExpandedCodeId]=useState(null);
  const [copiedCodeId,setCopiedCodeId]=useState(null);
  async function copyParentCode(playerId,code){
    if(!code)return;
    try{
      await navigator.clipboard.writeText(code);
      setCopiedCodeId(playerId);
      setTimeout(()=>setCopiedCodeId(id=>id===playerId?null:id),1800);
    }catch(e){
      console.error("Copy failed:",e);
    }
  }
  async function regenParentCode(playerId){
    const newCode=genCode();
    const res=await api.patch(token,"players",playerId,{parent_join_code:newCode});
    if(res&&res.length>0&&onParentCodeUpdated)onParentCodeUpdated(playerId,newCode);
  }
  const [genderFilter,setGenderFilter]=useState("all"); // all | male | female
  const [search,setSearch]=useState("");
  const [showForm,setShowForm]=useState(false);
  const [editId,setEditId]=useState(null);
  const [form,setForm]=useState({name:"",number:"",position:"CM",secondary_position:"",dob:"",gender:"male",nationality:"🏴󠁧󠁢󠁥󠁮󠁧󠁿"});
  const [formErr,setFormErr]=useState("");
  // Sorting the table. Kept as a key + direction rather than a comparator so the
  // header cells can render their own active/inactive arrow off the same state.
  const [sortKey,setSortKey]=useState("name"); // name | position | age | dob | teams
  const [sortDir,setSortDir]=useState(1);      // 1 asc, -1 desc
  const [confirmDelete,setConfirmDelete]=useState(null); // player pending deletion
  const [selPlayer,setSelPlayer]=useState(null);
  const [showImport,setShowImport]=useState(false);
  const [pDetailTab,setPDetailTab]=useState("overview"); // "overview" | "development"


  useEffect(()=>modalBackEffect(selPlayer!==null,()=>setSelPlayer(null)),[selPlayer!==null]);
  useEffect(()=>modalBackEffect(showForm,()=>setShowForm(false)),[showForm]);
  useEffect(()=>modalBackEffect(showImport,()=>setShowImport(false)),[showImport]);
  useEffect(()=>{ if(selPlayer&&onLoadPlayerAchievements)onLoadPlayerAchievements(selPlayer.id); },[selPlayer?.id]);
  useEffect(()=>{ if(selPlayer)setPDetailTab("overview"); },[selPlayer?.id]);

  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const inp={padding:"8px 10px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:13,outline:"none",boxSizing:"border-box"};
  const lbl={display:"block",fontSize:10,fontWeight:700,color:"var(--c-text)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em"};

  // Which teams (in this org) is a given player currently part of? Declared here
  // rather than below because the sort comparator needs it.
  function teamsFor(playerId){
    return teams.filter(t=>(t.playerIds||[]).includes(playerId));
  }

  // Sort values are pulled out per row rather than compared inline so that the
  // "missing" cases land somewhere sensible: a player with no DOB has no age to
  // compare, and dropping them to the end of the list in both directions would
  // hide them. They sort as if they were the oldest, which at least keeps them
  // adjacent to each other and easy to spot and fill in.
  function sortVal(p){
    switch(sortKey){
      case "position": return (p.position||"zzz").toLowerCase();
      case "age":      { const a=calcAge(p.dob); return a===null?-1:a; }
      case "dob":      return p.dob||"";
      case "teams":    return teamsFor(p.id).length;
      default:         return p.name.toLowerCase();
    }
  }
  const filtered = allPlayers
    .filter(p=>genderFilter==="all"||p.gender===genderFilter)
    .filter(p=>p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b)=>{
      const x=sortVal(a),y=sortVal(b);
      if(x<y)return -1*sortDir;
      if(x>y)return 1*sortDir;
      return a.name.localeCompare(b.name); // stable tie-break, always by name
    });

  function toggleSort(key){
    if(sortKey===key)setSortDir(d=>-d);
    else{setSortKey(key);setSortDir(1);}
  }

  function openAdd(){ setForm({name:"",number:"",position:"CM",secondary_position:"",dob:"",gender:"male",nationality:"🏴󠁧󠁢󠁥󠁮󠁧󠁿"}); setEditId(null); setFormErr(""); setShowForm(true); }
  function openEdit(p){ setForm({...p,dob:p.dob||"",number:String(p.number||""),secondary_position:p.secondary_position||""}); setEditId(p.id); setFormErr(""); setShowForm(true); }
  function save(){
    if(!form.name.trim()){setFormErr("Please enter the player's name.");return;}
    const n=parseJerseyNumber(form.number);
    if(n===undefined){setFormErr("Preferred number must be a whole number (0 or higher).");return;}
    if(form.secondary_position&&form.secondary_position===form.position){
      setFormErr("Secondary position must be different from the primary one.");return;
    }
    setFormErr("");
    // "" is what an empty <select> gives us, but the column is nullable text and
    // an empty string is not the same as "no secondary position" once it is read
    // back — every `p.secondary_position &&` check would still be falsy, but it
    // would show up in exports and filters as a real value. Normalise on the way out.
    const payload={...form,secondary_position:form.secondary_position||null};
    if(editId) onUpdatePlayer(editId,payload);
    else onAddPlayer(payload);
    setShowForm(false);
  }

  return(<div>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
      <div>
        <div style={{fontWeight:700,fontSize:isMobile?15:17}}>⚽ Player Base</div>
        <div style={{fontSize:11,color:"var(--c-muted)",marginTop:2}}>All players in your organisation. Add players here once — assign them to teams automatically by age & gender.</div>
      </div>
    </div>

    <div style={{display:"flex",gap:7,marginBottom:12,flexWrap:"wrap"}}>
      <input placeholder="Search players…" value={search} onChange={e=>setSearch(e.target.value)} style={{...inp,flex:1,minWidth:120}}/>
      <div style={{display:"flex",gap:3,background:AS.bgColor,borderRadius:8,padding:3}}>
        {[["all","All"],["male","♂ Male"],["female","♀ Female"]].map(([v,l])=>(
          <button key={v} onClick={()=>setGenderFilter(v)} style={{padding:"7px 12px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:genderFilter===v?ac:"transparent",color:genderFilter===v?"#fff":"var(--c-muted)"}}>{l}</button>
        ))}
      </div>
      <button onClick={openAdd} style={{padding:"8px 14px",borderRadius:7,border:"none",background:ac,color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer"}}>+ Add Player</button>
      <button onClick={()=>setShowImport(true)} style={{display:"flex",alignItems:"center",gap:5,padding:"8px 13px",borderRadius:7,border:`1px solid ${ac}55`,background:ac+"18",color:ac,fontSize:12,fontWeight:700,cursor:"pointer"}}>
        <span style={{fontSize:13}}>⬆</span> Import CSV
      </button>
    </div>

    {filtered.length===0?(
      <div style={{...C,padding:40,textAlign:"center",color:"var(--c-muted2)"}}>
        <div style={{fontSize:32,marginBottom:10}}>⚽</div>
        <div style={{fontSize:14,fontWeight:600,color:"var(--c-muted)",marginBottom:10}}>{allPlayers.length===0?"No players in your organisation yet":"No players match your filter"}</div>
        {allPlayers.length===0&&<button onClick={openAdd} style={{padding:"9px 20px",borderRadius:8,border:"none",background:ac,color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer"}}>+ Add First Player</button>}
      </div>
    ):(
      (()=>{
        // ── The player table ────────────────────────────────────────────────
        // One GRID string drives the header row and every data row, so a column
        // cannot drift out of line with its heading — the failure mode you get
        // from giving each row its own flex layout and hoping the widths agree.
        //
        // Fixed columns for the short, predictable values (position, age, DOB,
        // actions) and fr-columns for the two that vary (name, teams), with a
        // minWidth on the whole thing and overflowX on its wrapper. Below that
        // width the table scrolls sideways rather than reflowing into a second
        // mobile layout: a table that keeps its columns is still readable on a
        // phone, whereas a stacked card view is a second code path to maintain
        // and stops being a database the moment you want to compare two rows.
        const GRID="22px minmax(150px,1.7fr) 122px 56px 104px minmax(126px,1.4fr) 150px 64px";
        const rowBase={display:"grid",gridTemplateColumns:GRID,alignItems:"center",gap:11,padding:"10px 14px"};
        const hs={fontSize:9.5,fontWeight:800,color:"var(--c-muted)",textTransform:"uppercase",letterSpacing:"0.06em",background:"none",border:"none",padding:0,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:3,fontFamily:"inherit"};
        // The arrow only renders on the active column. Showing a greyed-out one
        // on every header reads as clutter at this size and, worse, as though
        // several columns were sorted at once.
        const TH=({k,label})=>(
          <button onClick={()=>toggleSort(k)} style={{...hs,color:sortKey===k?ac:"var(--c-muted)"}} title={`Sort by ${label.toLowerCase()}`}>
            {label}{sortKey===k&&<span style={{fontSize:8}}>{sortDir===1?"▲":"▼"}</span>}
          </button>
        );
        return(
        <div style={{...C,overflow:"hidden"}}>
          <div style={{overflowX:"auto"}}>
            <div style={{minWidth:900}}>
              <div style={{...rowBase,padding:"9px 14px",borderBottom:`1px solid ${AS.borderColor}`,background:AS.bgColor}}>
                <span/>
                <TH k="name" label={`Player (${filtered.length})`}/>
                <TH k="position" label="Position"/>
                <TH k="age" label="Age"/>
                <TH k="dob" label="DOB"/>
                <TH k="teams" label="Teams"/>
                <span style={{...hs,cursor:"default"}}>Parent Code</span>
                <span/>
              </div>
              {filtered.map(p=>{
                const age=calcAge(p.dob);
                const pTeams=teamsFor(p.id);
                return(
                  <div key={p.id} onClick={()=>setSelPlayer(p)} style={{...rowBase,borderTop:`1px solid ${AS.borderColor}`,cursor:"pointer",transition:"background 0.1s"}}
                    onMouseEnter={e=>e.currentTarget.style.background=AS.borderColor+"44"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <span style={{fontSize:13,color:"var(--c-muted)"}}>{p.gender==="female"?"♀":"♂"}</span>

                    <div style={{minWidth:0,fontWeight:600,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      <Flag value={p.nationality}/> {p.name}
                      {p.number!==null&&p.number!==undefined&&p.number!==""&&<span style={{color:"var(--c-muted2)",fontWeight:700,fontSize:11,marginLeft:6}}>#{p.number}</span>}
                    </div>

                    {/* Primary is filled in the accent colour, secondary is an
                        outline — the two have to be tellable apart at a glance
                        or the column just looks like a player with two positions
                        and no ranking between them. */}
                    <div style={{display:"flex",gap:4,alignItems:"center",minWidth:0}}>
                      <span style={{fontSize:9.5,fontWeight:800,padding:"2px 6px",borderRadius:5,background:ac+"1f",color:ac,flexShrink:0}}>{p.position||"—"}</span>
                      {p.secondary_position&&<span title="Secondary position" style={{fontSize:9.5,fontWeight:700,padding:"2px 6px",borderRadius:5,background:"none",color:"var(--c-muted)",border:`1px solid ${AS.borderColor}`,flexShrink:0}}>{p.secondary_position}</span>}
                    </div>

                    <span style={{fontSize:12,fontWeight:600,color:age===null?"var(--c-muted2)":"var(--c-text)"}}>{age!==null?age.toFixed(1):"—"}</span>
                    <span style={{fontSize:11,color:p.dob?"var(--c-muted)":"var(--c-muted2)",whiteSpace:"nowrap"}}>{p.dob?fmtDOB(p.dob):"—"}</span>

                    <div style={{display:"flex",gap:4,flexWrap:"wrap",minWidth:0}}>
                      {pTeams.length===0?(
                        <span style={{fontSize:9,color:"var(--c-muted2)",fontStyle:"italic"}}>No team yet</span>
                      ):pTeams.map(t=>(
                        <span key={t.id} style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:5,background:t.color+"22",color:t.color,whiteSpace:"nowrap"}}>{t.badge} {t.age_category}</span>
                      ))}
                    </div>

                    <div style={{minWidth:0}}>
                    {!entitlements.parentAccounts?(
                      <button onClick={e=>{e.stopPropagation();alert("Your free trial has ended. Subscribe from the Plan page to re-enable parent accounts.");}} style={{fontSize:9,fontWeight:700,padding:"3px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted2)",cursor:"pointer",whiteSpace:"nowrap"}}>🔒 Parent Code</button>
                    ):expandedCodeId===p.id?(
                      <div onClick={e=>e.stopPropagation()} style={{display:"flex",alignItems:"center",gap:4,background:AS.bgColor,borderRadius:6,padding:"3px 6px"}}>
                        <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.06em",color:"#8b5cf6"}}>{p.parent_join_code||"—"}</span>
                        <button onClick={()=>copyParentCode(p.id,p.parent_join_code)} title="Copy to clipboard" style={{background:"none",border:"none",color:copiedCodeId===p.id?"#22c55e":"var(--c-muted)",cursor:"pointer",fontSize:10,fontWeight:copiedCodeId===p.id?700:400,padding:0}}>{copiedCodeId===p.id?"✓":"📋"}</button>
                        <button onClick={()=>regenParentCode(p.id)} title="Regenerate" style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:11,padding:0}}>↻</button>
                        <button onClick={()=>setExpandedCodeId(null)} title="Hide" style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:11,padding:0}}>✕</button>
                      </div>
                    ):(
                      <button onClick={e=>{e.stopPropagation();setExpandedCodeId(p.id);if(!p.parent_join_code)regenParentCode(p.id);}} style={{fontSize:9,fontWeight:700,padding:"3px 8px",borderRadius:5,border:"1px solid #8b5cf655",background:"none",color:"#8b5cf6",cursor:"pointer",whiteSpace:"nowrap"}}>👪 Parent Code</button>
                    )}
                    </div>

                    <div style={{display:"flex",gap:4,justifyContent:"flex-end"}}>
                      <button onClick={e=>{e.stopPropagation();openEdit(p);}} title={`Edit ${p.name}`} style={{background:"none",border:`1px solid ${AS.borderColor}`,borderRadius:5,color:"var(--c-muted)",cursor:"pointer",padding:"3px 6px",fontSize:10,lineHeight:1.4}}>✏️</button>
                      <button onClick={e=>{e.stopPropagation();setConfirmDelete(p);}} title={`Delete ${p.name}`} style={{background:"none",border:"1px solid #ef444455",borderRadius:5,color:"#ef4444",cursor:"pointer",padding:"3px 6px",fontSize:11,fontWeight:700,lineHeight:1.4}}>🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>);
      })()
    )}

    {/* Player detail (overall stats across all teams) */}
    {selPlayer&&(()=>{
      const pTeams=teamsFor(selPlayer.id);
      const pGroups=(groups||[]).filter(g=>(g.playerIds||[]).includes(selPlayer.id));
      const overall=pTeams.reduce((acc,t)=>{
        const tp=(t.playerStats||{})[selPlayer.id]||{goals:0,assists:0};
        return{goals:acc.goals+(tp.goals||0),assists:acc.assists+(tp.assists||0)};
      },{goals:0,assists:0});
      const att=orgAttendance?.[selPlayer.id];
      function pct(bucket){ return bucket&&bucket.total>0?Math.round(((bucket.present+bucket.late)/bucket.total)*100):null; }
      const matchRate=pct(att?.byType?.game);
      const trainingRate=pct(att?.byType?.training);
      const age=calcAge(selPlayer.dob);
      return(
        <div onClick={()=>setSelPlayer(null)} style={{position:"fixed",inset:0,bottom:isMobile?"var(--nav-h,65px)":0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:50,padding:isMobile?0:14}}>
          <div onClick={e=>e.stopPropagation()} style={{background:AS.surfaceColor,borderRadius:isMobile?"16px 16px 0 0":13,padding:22,width:"100%",maxWidth:isMobile?"100%":400,border:`1px solid ${AS.borderColor}`,maxHeight:"85vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div style={{display:"flex",gap:12,alignItems:"center"}}>
                <PlayerPhotoUploader playerId={selPlayer.id} photoUrl={selPlayer.photo_url} name={selPlayer.name} size={56} ac={ac} token={token} canEdit={true} onUploaded={url=>{setSelPlayer(p=>({...p,photo_url:url}));onPlayerPhotoUpdated&&onPlayerPhotoUpdated(selPlayer.id,url);}}/>
                <div>
                  <div style={{fontSize:11,color:"var(--c-muted)",marginBottom:3}}>{selPlayer.position}{selPlayer.secondary_position?` / ${selPlayer.secondary_position}`:""} · {selPlayer.gender==="female"?"♀ Female":"♂ Male"}</div>
                  <div style={{fontSize:19,fontWeight:700}}><Flag value={selPlayer.nationality}/> {selPlayer.name}</div>
                  <div style={{display:"flex",gap:12,marginTop:5}}>
                    {selPlayer.dob&&<span style={{fontSize:11,color:"var(--c-muted)"}}>🎂 {fmtDOB(selPlayer.dob)}</span>}
                    {age!==null&&<span style={{fontSize:11,color:"var(--c-text2)"}}>Age <strong style={{color:ac}}>{age.toFixed(1)}</strong></span>}
                  </div>
                </div>
              </div>
              <button onClick={()=>setSelPlayer(null)} style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:18}}>×</button>
            </div>

            {(org?.feature_player_development!==false)&&(
              <div style={{display:"flex",gap:3,background:AS.bgColor,borderRadius:8,padding:3,marginBottom:16}}>
                {[["overview","Overview"],["development","📈 Development"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setPDetailTab(v)} style={{flex:1,padding:"7px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:pDetailTab===v?ac:"transparent",color:pDetailTab===v?"#fff":"var(--c-muted)"}}>{l}</button>
                ))}
              </div>
            )}

            {pDetailTab==="overview"&&(<>
            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Overall Stats (all teams combined)</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:12}}>
              {[["Goals",overall.goals,ac],["Assists",overall.assists,"#8b5cf6"]].map(([l,v,c])=>(
                <div key={l} style={{background:AS.bgColor,borderRadius:9,padding:"12px 8px",textAlign:"center"}}>
                  <div style={{fontSize:19,fontWeight:700,color:c}}>{v}</div>
                  <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>{l}</div>
                </div>
              ))}
            </div>

            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Attendance</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:16}}>
              {[["Match Attendance",matchRate],["Training Attendance",trainingRate]].map(([l,r])=>(
                <div key={l} style={{background:AS.bgColor,borderRadius:9,padding:"12px 8px",textAlign:"center"}}>
                  <div style={{fontSize:19,fontWeight:700,color:r===null?"var(--c-muted2)":r>=80?"#22c55e":r>=50?"#eab308":"#ef4444"}}>{r!==null?`${r}%`:"—"}</div>
                  <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>{l}</div>
                </div>
              ))}
            </div>
            {orgAttendance===null&&<div style={{fontSize:10,color:"var(--c-muted2)",marginTop:-10,marginBottom:16}}>Loading attendance…</div>}

            {org?.feature_achievements!==false&&(<>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>🏅 Achievements</div>
              {onAwardAchievement&&<button onClick={()=>onAwardAchievement({scope:"individual",id:selPlayer.id,name:selPlayer.name})} style={{fontSize:10,fontWeight:700,color:ac,background:"none",border:"none",cursor:"pointer"}}>+ Award</button>}
            </div>
            <div style={{marginBottom:16}}><AchievementBadges rows={playerAchievements?.[selPlayer.id]}/></div>
            </>)}

            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Team Membership</div>
            {pTeams.length===0?(
              <div style={{fontSize:12,color:"var(--c-muted2)",marginBottom:16}}>Not assigned to any team yet.</div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:16}}>
                {pTeams.map(t=>{
                  const tp=(t.playerStats||{})[selPlayer.id]||{goals:0,assists:0};
                  return(
                    <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:AS.bgColor,borderRadius:8}}>
                      <span style={{fontSize:14}}>{t.badge}</span>
                      <span style={{flex:1,fontWeight:600,fontSize:12}}>{t.name}</span>
                      <span title="Goals · Assists" style={{fontSize:10,color:"var(--c-muted)"}}>{tp.goals||0}G · {tp.assists||0}A</span>
                    </div>
                  );
                })}
              </div>
            )}

            {pGroups.length>0&&<>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Training Groups</div>
              <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:16}}>
                {pGroups.map(g=>(
                  <div key={g.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:AS.bgColor,borderRadius:8}}>
                    <span style={{fontSize:14}}>{g.badge||"🏋️"}</span>
                    <span style={{flex:1,fontWeight:600,fontSize:12}}>{g.name}</span>
                  </div>
                ))}
              </div>
            </>}
            </>)}

            {pDetailTab==="development"&&org?.feature_player_development!==false&&(
              <div style={{marginBottom:16}}><PlayerDevelopmentSection playerId={selPlayer.id} token={token} coachId={coachId} canEdit={true} ac={ac} AS={AS}/></div>
            )}

            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>onOpenNotes(selPlayer)} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:ac,color:"#fff",cursor:"pointer",fontWeight:700,fontSize:13,boxShadow:"0 1px 3px rgba(0,0,0,0.15)"}}>📝 Contact</button>
              <button onClick={()=>openEdit(selPlayer)} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:"var(--c-muted2)",color:"#fff",cursor:"pointer",fontWeight:700,fontSize:13,boxShadow:"0 1px 3px rgba(0,0,0,0.15)"}}>Edit</button>
              {/* This used to delete the player outright on a single click, with
                  no confirmation and nothing to undo it — one mis-tap next to
                  "Edit" and a season of their stats was gone. It now goes through
                  the same confirmation dialog as the table's delete button. */}
              <button onClick={()=>setConfirmDelete(selPlayer)} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:"#ef4444",color:"#fff",cursor:"pointer",fontWeight:700,fontSize:13,boxShadow:"0 1px 3px rgba(0,0,0,0.15)"}}>Remove</button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* Add/Edit form */}
    {showForm&&(
      <div onClick={()=>setShowForm(false)} style={{position:"fixed",inset:0,bottom:isMobile?"var(--nav-h,65px)":0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:50,padding:isMobile?0:14}}>
        <div onClick={e=>e.stopPropagation()} style={{background:AS.surfaceColor,borderRadius:isMobile?"16px 16px 0 0":13,width:"100%",maxWidth:isMobile?"100%":420,border:`1px solid ${AS.borderColor}`,maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
          <div style={{padding:"18px 22px 14px",borderBottom:`1px solid ${AS.borderColor}`,flexShrink:0}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <h2 style={{margin:0,fontSize:15,fontWeight:700}}>{editId?"Edit Player":"Add Player to Organisation"}</h2>
              <button onClick={()=>setShowForm(false)} style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:18}}>×</button>
            </div>
          </div>
          <div style={{overflowY:"auto",flex:1,padding:"16px 22px",display:"flex",flexDirection:"column",gap:10}}>
            <div><label style={lbl}>Name</label><input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Full name" style={{...inp,width:"100%"}}/></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
              <div><label style={lbl}>Preferred Number</label><input type="number" min="0" step="1" value={form.number} onChange={e=>setForm(f=>({...f,number:e.target.value}))} placeholder="Optional" style={{...inp,width:"100%"}}/></div>
              <div><label style={lbl}>Nationality</label><FlagPicker value={form.nationality} onChange={v=>setForm(f=>({...f,nationality:v}))} style={{...inp,width:"100%"}}/></div>
            </div>
            <div>
              <label style={lbl}>Gender</label>
              <div style={{display:"flex",gap:7}}>
                {[["male","♂ Male"],["female","♀ Female"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setForm(f=>({...f,gender:v}))} style={{flex:1,padding:"9px",borderRadius:7,cursor:"pointer",fontSize:12,fontWeight:700,background:form.gender===v?ac:"var(--c-bg0)",color:form.gender===v?"#fff":"var(--c-muted)",border:form.gender===v?`1px solid ${ac}`:"1px solid var(--c-border)"}}>{l}</button>
                ))}
              </div>
            </div>
            <div><label style={lbl}>Date of Birth</label>
              <input type="date" value={form.dob||""} onChange={e=>setForm(f=>({...f,dob:e.target.value}))} style={{...inp,width:"100%",colorScheme:mode}}/>
              {form.dob&&calcAge(form.dob)!==null&&<div style={{marginTop:4,fontSize:11,color:"var(--c-text2)"}}>Age: <strong style={{color:ac}}>{calcAge(form.dob).toFixed(1)}</strong></div>}
              {!form.dob&&<div style={{marginTop:4,fontSize:11,color:"#f59e0b"}}>⚠ No date of birth set — age-based team eligibility won't work for this player until it's added.</div>}
            </div>
            {/* Primary and secondary sit side by side so it is obvious they are
                the same kind of thing with a ranking between them, rather than
                the secondary looking like an unrelated extra field further down.
                The secondary lists every position except the one already chosen
                as primary — offering "CM and also CM" is meaningless, and the
                cheapest place to prevent it is the control itself. */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
              <div><label style={lbl}>Primary Position</label>
                <select value={form.position} onChange={e=>setForm(f=>({...f,position:e.target.value,secondary_position:f.secondary_position===e.target.value?"":f.secondary_position}))} style={{...inp,width:"100%",cursor:"pointer"}}>{POSITIONS.map(p=><option key={p}>{p}</option>)}</select>
              </div>
              <div><label style={lbl}>Secondary Position</label>
                <select value={form.secondary_position||""} onChange={e=>setForm(f=>({...f,secondary_position:e.target.value}))} style={{...inp,width:"100%",cursor:"pointer"}}>
                  <option value="">— None —</option>
                  {POSITIONS.filter(x=>x!==form.position).map(x=><option key={x} value={x}>{x}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div style={{padding:"12px 22px",borderTop:`1px solid ${AS.borderColor}`,flexShrink:0,paddingBottom:"calc(12px + env(safe-area-inset-bottom, 0px))"}}>
            {formErr&&<div style={{color:"#ef4444",fontSize:11,fontWeight:700,marginBottom:8}}>{formErr}</div>}
            <button onClick={save} style={{width:"100%",padding:"12px",borderRadius:9,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:14,cursor:"pointer"}}>{editId?"Save Changes":"Add to Player Base"}</button>
          </div>
        </div>
      </div>
    )}

    {/* Delete confirmation.

        Deleting a player is not recoverable from inside the app — the row goes
        and their goals, assists, attendance history and notes go with it — so
        the dialog says exactly that, names the player, and lists the teams they
        will disappear from. A bare "Are you sure?" gets clicked through without
        being read; naming the consequences is what actually gives someone the
        chance to stop.

        Cancel is the visually dominant button and Delete is the plain one. The
        destructive action being the easiest to hit is how accidents happen. */}
    {confirmDelete&&(()=>{
      const dTeams=teamsFor(confirmDelete.id);
      return(
      <div onClick={()=>setConfirmDelete(null)} style={{position:"fixed",inset:0,bottom:isMobile?"var(--nav-h,65px)":0,background:"rgba(0,0,0,0.65)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:70,padding:isMobile?0:14}}>
        <div onClick={e=>e.stopPropagation()} style={{background:AS.surfaceColor,borderRadius:isMobile?"16px 16px 0 0":13,width:"100%",maxWidth:isMobile?"100%":410,border:`1px solid ${AS.borderColor}`,overflow:"hidden"}}>
          <div style={{padding:"18px 22px 0"}}>
            <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:10}}>
              <div style={{width:30,height:30,borderRadius:8,background:"#ef444422",color:"#ef4444",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>⚠</div>
              <h2 style={{margin:0,fontSize:15,fontWeight:800}}>Delete {confirmDelete.name}?</h2>
            </div>
            <div style={{fontSize:12.5,color:"var(--c-text2)",lineHeight:1.55}}>
              This permanently removes them from your player base. Their goals, assists,
              attendance record, notes and any linked parent account go with them, and
              this cannot be undone from inside pitchMGR.
            </div>
            {dTeams.length>0&&(
              <div style={{marginTop:11,padding:"9px 11px",borderRadius:8,background:AS.bgColor,border:`1px solid ${AS.borderColor}`}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--c-muted)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>They will be removed from</div>
                <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                  {dTeams.map(t=>(
                    <span key={t.id} style={{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:5,background:t.color+"22",color:t.color}}>{t.badge} {t.name||t.age_category}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:8,padding:"16px 22px",paddingBottom:"calc(16px + env(safe-area-inset-bottom, 0px))"}}>
            <button onClick={()=>setConfirmDelete(null)} style={{flex:1,padding:"11px",borderRadius:9,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer"}}>Keep player</button>
            <button onClick={()=>{onDeletePlayer(confirmDelete.id);setSelPlayer(null);setConfirmDelete(null);}} style={{flex:1,padding:"11px",borderRadius:9,border:"1px solid #ef4444",background:"none",color:"#ef4444",fontWeight:700,fontSize:13,cursor:"pointer"}}>Delete permanently</button>
          </div>
        </div>
      </div>);
    })()}

    {/* CSV Import modal */}
    {showImport&&(
      <ImportPlayersModal onClose={()=>setShowImport(false)} onImport={onBulkAddPlayers} ac={ac} AS={AS} isMobile={isMobile}/>
    )}
  </div>);
}

// ── ImportPlayersModal ───────────────────────────────────────────────────────

export function ImportPlayersModal({onClose,onImport,ac,AS,isMobile}){
  const [fileName,setFileName]=useState("");
  const [parsed,setParsed]=useState(null); // {rows, errors}
  const [importing,setImporting]=useState(false);
  const [importError,setImportError]=useState("");
  const [done,setDone]=useState(false);
  const fileRef=useRef(null);

  function handleFile(e){
    const file=e.target.files[0];
    if(!file)return;
    setFileName(file.name);
    const reader=new FileReader();
    reader.onload=ev=>{
      const result=parsePlayerCSV(String(ev.target.result));
      setParsed(result);
    };
    reader.readAsText(file);
  }

  async function doImport(){
    if(!parsed||parsed.rows.length===0)return;
    setImporting(true);
    setImportError("");
    try{
      const result=await onImport(parsed.rows);
      if(!result || !Array.isArray(result)){
        setImportError("Import failed — the server didn't accept the data. Try removing any unusual characters from the Nationality column and try again.");
        setImporting(false);
        return;
      }
      setImporting(false);
      setDone(true);
    } catch(err){
      console.error("Import error:",err);
      setImportError("Import failed: "+(err.message||String(err)));
      setImporting(false);
    }
  }

  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const lbl={display:"block",fontSize:10,fontWeight:700,color:"var(--c-text)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em"};

  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,bottom:isMobile?"var(--nav-h,65px)":0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:60,padding:isMobile?0:14}}>
      <div onClick={e=>e.stopPropagation()} style={{background:AS.surfaceColor,borderRadius:isMobile?"16px 16px 0 0":13,width:"100%",maxWidth:isMobile?"100%":480,border:`1px solid ${AS.borderColor}`,maxHeight:"88vh",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"18px 22px 14px",borderBottom:`1px solid ${AS.borderColor}`,flexShrink:0}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h2 style={{margin:0,fontSize:15,fontWeight:700}}>⬆ Import Players from CSV</h2>
            <button onClick={onClose} style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:18}}>×</button>
          </div>
        </div>

        <div style={{overflowY:"auto",flex:1,padding:"16px 22px",display:"flex",flexDirection:"column",gap:14}}>
          {done?(
            <div style={{textAlign:"center",padding:"30px 10px"}}>
              <div style={{fontSize:36,marginBottom:10}}>✅</div>
              <div style={{fontSize:15,fontWeight:700,marginBottom:6}}>{parsed.rows.length} player{parsed.rows.length!==1?"s":""} imported!</div>
              <div style={{fontSize:12,color:"var(--c-muted)"}}>They're now in your Player Base, ready to be added to teams.</div>
            </div>
          ):(<>
            {/* Template download */}
            <div style={{...C,padding:14}}>
              <div style={{fontSize:12,fontWeight:600,marginBottom:6}}>Need a starting point?</div>
              <div style={{fontSize:11,color:"var(--c-muted)",marginBottom:10}}>Download a CSV template with the right column headers and an example row.</div>
              <button onClick={downloadPlayerCSVTemplate} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:8,border:`1px solid ${ac}55`,background:ac+"18",color:ac,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                <span>⬇</span> Download Template CSV
              </button>
            </div>

            {/* File picker */}
            <div>
              <label style={lbl}>Upload Your CSV</label>
              <button onClick={()=>fileRef.current?.click()} style={{width:"100%",padding:"14px",borderRadius:9,border:`1px dashed ${AS.borderColor}`,background:AS.bgColor,color:fileName?"var(--c-text3)":"var(--c-muted)",fontSize:12,cursor:"pointer",textAlign:"center"}}>
                {fileName?`📄 ${fileName}`:"Click to choose a CSV file"}
              </button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" style={{display:"none"}} onChange={handleFile}/>
            </div>

            {/* Validation errors */}
            {parsed&&parsed.errors.length>0&&(
              <div style={{background:"#7c2d1240",border:"1px solid #f59e0b55",borderRadius:9,padding:"10px 12px"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#f59e0b",marginBottom:5}}>⚠ {parsed.errors.length} issue{parsed.errors.length!==1?"s":""} found</div>
                {parsed.errors.slice(0,6).map((e,i)=>(<div key={i} style={{fontSize:11,color:"#fbbf24",marginBottom:2}}>{e}</div>))}
                {parsed.errors.length>6&&<div style={{fontSize:11,color:"#fbbf24"}}>…and {parsed.errors.length-6} more</div>}
              </div>
            )}

            {/* Preview */}
            {parsed&&parsed.rows.length>0&&(
              <div>
                <label style={lbl}>Preview ({parsed.rows.length} player{parsed.rows.length!==1?"s":""})</label>
                <div style={{...C,maxHeight:220,overflowY:"auto"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 50px 50px 30px",gap:6,padding:"6px 10px",fontSize:9,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",borderBottom:`1px solid ${AS.borderColor}`}}>
                    <span>Name</span><span>Pos</span><span>DOB</span><span></span>
                  </div>
                  {parsed.rows.map((r,i)=>(
                    <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 50px 50px 30px",gap:6,padding:"6px 10px",fontSize:11,borderTop:i>0?`1px solid ${AS.bgColor}`:"none"}}>
                      <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><Flag value={r.nationality}/> {r.name}</span>
                      <span style={{color:"#93c5fd"}}>{r.position}</span>
                      <span style={{color:"var(--c-muted)",fontSize:10}}>{r.dob||"—"}</span>
                      <span>{r.gender==="female"?"♀":"♂"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {parsed&&parsed.rows.length===0&&parsed.errors.length>0&&(
              <div style={{fontSize:12,color:"var(--c-muted2)",textAlign:"center",padding:10}}>No valid player rows could be read from this file. Check the template format and try again.</div>
            )}
          </>)}
        </div>

        <div style={{padding:"14px 22px",borderTop:`1px solid ${AS.borderColor}`,flexShrink:0,paddingBottom:"calc(14px + env(safe-area-inset-bottom, 0px))"}}>
          {importError&&<div style={{background:"#fee2e2",color:"#991b1b",padding:"9px 11px",borderRadius:7,fontSize:12,marginBottom:10}}>{importError}</div>}
          {done?(
            <button onClick={onClose} style={{width:"100%",padding:"12px",borderRadius:9,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:14,cursor:"pointer"}}>Done</button>
          ):(
            <button disabled={!parsed||parsed.rows.length===0||importing} onClick={doImport} style={{width:"100%",padding:"12px",borderRadius:9,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:14,cursor:"pointer",opacity:(!parsed||parsed.rows.length===0||importing)?0.5:1}}>
              {importing?"Importing…":parsed&&parsed.rows.length>0?`Import ${parsed.rows.length} Player${parsed.rows.length!==1?"s":""} →`:"Choose a file to import"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CoachBaseView ─────────────────────────────────────────────────────────────
// Mirrors Player Base: every coach in the organisation, with the head coach able
// to assign/unassign them to specific teams they're allowed to manage.

export function CoachBaseView({coaches, teams, groups, token, isHead, myCoachId, ownerId, onAssign, onUnassign, onSetAdmin, onTransferOwnership, ac, AS, isMobile}){
  const [confirmTransferId,setConfirmTransferId]=useState(null);
  const [search,setSearch]=useState("");
  const [expandedId,setExpandedId]=useState(null);
  const [sessionLog,setSessionLog]=useState(null); // null=loading, [] once loaded
  const [coachFilter,setCoachFilter]=useState("all"); // "all" | coach id

  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};

  useEffect(()=>{
    let cancelled=false;
    async function loadLog(){
      const teamIds=(teams||[]).map(t=>t.id);
      const groupIds=(groups||[]).map(g=>g.id);
      const [teamSess,groupSess]=await Promise.all([
        teamIds.length>0?api.get(token,"sessions","team_id=in.("+teamIds.join(",")+")&order=date.desc"):Promise.resolve([]),
        groupIds.length>0?api.get(token,"group_sessions","group_id=in.("+groupIds.join(",")+")&order=date.desc"):Promise.resolve([])
      ]);
      if(cancelled)return;
      const combined=[
        ...(teamSess||[]).map(s=>({...s,_kind:"team",_scopeName:(teams||[]).find(t=>t.id===s.team_id)?.name||"Team"})),
        ...(groupSess||[]).map(s=>({...s,_kind:"group",_scopeName:(groups||[]).find(g=>g.id===s.group_id)?.name||"Squad"}))
      ].sort((a,b)=>(b.date||"").localeCompare(a.date||""));
      setSessionLog(combined);
    }
    loadLog();
    return ()=>{cancelled=true;};
  },[teams,groups,token]);

  const coachSessionCounts=(sessionLog||[]).reduce((acc,s)=>{
    (s.coach_ids||[]).forEach(id=>{acc[id]=(acc[id]||0)+1;});
    return acc;
  },{});

  const visibleSessionLog=coachFilter==="all"?(sessionLog||[]):(sessionLog||[]).filter(s=>(s.coach_ids||[]).includes(coachFilter));

  const monthlySummary=(()=>{
    const byMonth={};
    visibleSessionLog.forEach(s=>{
      if(!s.date)return;
      const key=s.date.slice(0,7); // YYYY-MM
      if(!byMonth[key])byMonth[key]={training:0,matches:0};
      if(s.type==="game")byMonth[key].matches++;
      else byMonth[key].training++;
    });
    return Object.entries(byMonth).sort((a,b)=>b[0].localeCompare(a[0])).map(([key,counts])=>{
      const d=new Date(key+"-01T00:00:00");
      const label=isNaN(d)?key:d.toLocaleDateString("en-GB",{month:"long",year:"numeric"});
      return {key,label,...counts};
    });
  })();

  const filtered = coaches
    .filter(c=>c.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b)=>{
      if(a.teamRole==="head"&&b.teamRole!=="head")return -1;
      if(b.teamRole==="head"&&a.teamRole!=="head")return 1;
      return a.name.localeCompare(b.name);
    });

  return(<div>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
      <div>
        <div style={{fontWeight:700,fontSize:isMobile?15:17}}>🧑‍🏫 Coaches Base</div>
        <div style={{fontSize:11,color:"var(--c-muted)",marginTop:2}}>
          {isHead?"Everyone who has joined your organisation. Assign each coach to the team(s) they manage.":"Coaches in your organisation. You can manage the teams the head coach has assigned to you."}
        </div>
      </div>
    </div>

    <input placeholder="Search coaches…" value={search} onChange={e=>setSearch(e.target.value)} style={{padding:"8px 10px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:13,outline:"none",boxSizing:"border-box",width:"100%",marginBottom:12}}/>

    {filtered.length===0?(
      <div style={{...C,padding:36,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No coaches found.</div>
    ):(
      <div style={{...C,overflow:"hidden"}}>
        <div style={{padding:"8px 12px",borderBottom:`1px solid ${AS.borderColor}`,fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Coaches ({filtered.length})</div>
        {filtered.map(c=>{
          const assignedTeams=teams.filter(t=>(c.assignedTeamIds||[]).includes(t.id));
          const isExpanded=expandedId===c.id;
          const canManageThisCoach=isHead&&c.id!==myCoachId;
          const isOwner=c.id===ownerId;
          const viewerIsOwner=myCoachId===ownerId;
          return(
            <div key={c.id} style={{borderTop:`1px solid ${AS.borderColor}`}}>
              <div onClick={()=>canManageThisCoach&&setExpandedId(isExpanded?null:c.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 14px",cursor:canManageThisCoach?"pointer":"default"}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:ac+"33",border:`2px solid ${ac}55`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,color:ac,flexShrink:0}}>{c.name.charAt(0).toUpperCase()}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:12}}>{isOwner&&"👑 "}{c.name} {c.id===myCoachId&&<span style={{fontSize:10,color:"var(--c-muted)"}}>(you)</span>}</div>
                  <div style={{fontSize:10,color:"var(--c-muted)"}}>{c.email}</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3}}>
                  <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:6,background:c.teamRole==="head"?ac+"22":"var(--c-border)",color:c.teamRole==="head"?ac:"var(--c-muted)"}}>{isOwner?"Owner":c.teamRole==="head"?"Admin":"Assistant"}</span>
                  {c.teamRole!=="head"&&(
                    assignedTeams.length===0?(
                      <span style={{fontSize:9,color:"var(--c-muted2)",fontStyle:"italic"}}>No teams assigned</span>
                    ):(
                      <span style={{fontSize:9,color:"var(--c-muted)"}}>{assignedTeams.length} team{assignedTeams.length!==1?"s":""}</span>
                    )
                  )}
                </div>
                {canManageThisCoach&&<span style={{color:"var(--c-muted)",fontSize:11,marginLeft:4}}>{isExpanded?"▲":"▼"}</span>}
              </div>
              {/* Assigned team chips (always visible, read-only summary) */}
              {c.teamRole!=="head"&&assignedTeams.length>0&&!isExpanded&&(
                <div style={{padding:"0 14px 10px",display:"flex",gap:5,flexWrap:"wrap"}}>
                  {assignedTeams.map(t=>(
                    <span key={t.id} style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:5,background:t.color+"22",color:t.color}}>{t.badge} {t.name}</span>
                  ))}
                </div>
              )}
              {/* Expanded editor — head coach only */}
              {canManageThisCoach&&isExpanded&&(
                <div style={{padding:"4px 14px 14px"}}>
                  <div style={{marginBottom:c.teamRole==="head"?0:14,display:"flex",flexDirection:"column",gap:8}}>
                    {isOwner?(
                      <div style={{fontSize:11,color:"var(--c-muted)",display:"flex",alignItems:"center",gap:6}}>👑 Organisation owner — can't be removed as admin. Only they can transfer ownership.</div>
                    ):(
                      <button onClick={()=>onSetAdmin(c,c.teamRole!=="head")} style={{padding:"7px 13px",borderRadius:7,border:c.teamRole==="head"?`1px solid ${AS.borderColor}`:"none",background:c.teamRole==="head"?"none":ac,color:c.teamRole==="head"?"var(--c-muted)":"#fff",fontSize:11,fontWeight:700,cursor:"pointer",alignSelf:"flex-start"}}>{c.teamRole==="head"?"Remove Admin":"⭐ Make Admin"}</button>
                    )}
                    {c.teamRole==="head"&&!isOwner&&<div style={{fontSize:10,color:"var(--c-muted2)"}}>Admins have full head-coach powers across the whole organisation.</div>}
                    {viewerIsOwner&&!isOwner&&(
                      confirmTransferId===c.id?(
                        <div style={{background:"var(--c-bg0)",borderRadius:8,padding:10,border:"1px solid #f59e0b55"}}>
                          <div style={{fontSize:11,color:"#f59e0b",fontWeight:700,marginBottom:8}}>Make {c.name} the organisation owner? You'll remain an admin, but only they will be able to transfer ownership after this.</div>
                          <div style={{display:"flex",gap:7}}>
                            <button onClick={()=>{onTransferOwnership(c);setConfirmTransferId(null);}} style={{padding:"6px 12px",borderRadius:6,border:"none",background:"#f59e0b",color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>Yes, Transfer</button>
                            <button onClick={()=>setConfirmTransferId(null)} style={{padding:"6px 12px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:11,cursor:"pointer"}}>Cancel</button>
                          </div>
                        </div>
                      ):(
                        <button onClick={()=>setConfirmTransferId(c.id)} style={{padding:"7px 13px",borderRadius:7,border:"1px solid #f59e0b55",background:"none",color:"#f59e0b",fontSize:11,fontWeight:700,cursor:"pointer",alignSelf:"flex-start"}}>👑 Transfer Ownership</button>
                      )
                    )}
                  </div>
                  {c.teamRole!=="head"&&(<>
                  <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Assign to teams</div>
                  {teams.length===0?(
                    <div style={{fontSize:11,color:"var(--c-muted2)"}}>No teams created yet.</div>
                  ):(
                    <div style={{display:"flex",flexDirection:"column",gap:5}}>
                      {teams.map(t=>{
                        const isAssigned=(c.assignedTeamIds||[]).includes(t.id);
                        return(
                          <button key={t.id} onClick={()=>isAssigned?onUnassign(c,t.id):onAssign(c,t.id)} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:AS.bgColor,borderRadius:8,border:isAssigned?`1px solid ${t.color}`:"1px solid transparent",cursor:"pointer",textAlign:"left"}}>
                            <span style={{fontSize:14}}>{t.badge}</span>
                            <span style={{flex:1,fontSize:12,fontWeight:600,color:isAssigned?"var(--c-text)":"var(--c-text2)"}}>{t.name}</span>
                            <span style={{fontSize:10,color:"var(--c-muted)"}}>{t.gender==="female"?"♀":"♂"}{t.age_category}</span>
                            <span style={{fontSize:14,color:isAssigned?t.color:"var(--c-muted2)"}}>{isAssigned?"✓":"+"}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  </>)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}

    <div style={{marginTop:22,paddingTop:18,borderTop:`1px solid ${AS.borderColor}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8,marginBottom:2}}>
        <div style={{fontWeight:700,fontSize:isMobile?14:15}}>📋 Coach Session Log</div>
        <select value={coachFilter} onChange={e=>setCoachFilter(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
          <option value="all">All Coaches</option>
          {coaches.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div style={{fontSize:11,color:"var(--c-muted)",marginBottom:10}}>Every training or match session logged for attendance, and which coach(es) were involved.</div>
      {sessionLog===null?(
        <div style={{...C,padding:24,textAlign:"center",color:"var(--c-muted2)",fontSize:11}}>Loading sessions…</div>
      ):sessionLog.length===0?(
        <div style={{...C,padding:24,textAlign:"center",color:"var(--c-muted2)",fontSize:11}}>No sessions logged yet.</div>
      ):(<>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
          {coaches.map(c=>(
            <span key={c.id} style={{fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:6,background:coachFilter===c.id?ac:ac+"18",color:coachFilter===c.id?"#fff":ac,cursor:"pointer"}} onClick={()=>setCoachFilter(coachFilter===c.id?"all":c.id)}>{c.name}: {coachSessionCounts[c.id]||0} session{(coachSessionCounts[c.id]||0)!==1?"s":""}</span>
          ))}
        </div>

        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>Monthly Summary{coachFilter!=="all"&&` — ${coaches.find(c=>c.id===coachFilter)?.name||""}`}</div>
        {monthlySummary.length===0?(
          <div style={{...C,padding:16,textAlign:"center",color:"var(--c-muted2)",fontSize:11,marginBottom:14}}>No sessions for this selection.</div>
        ):(
          <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:14}}>
            {monthlySummary.map(m=>(
              <div key={m.key} style={{...C,padding:"8px 12px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <div style={{fontWeight:700,fontSize:11,minWidth:130}}>{m.label}</div>
                <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:5,background:"#14532d44",color:"#4ade80"}}>🏃 Training {m.training}</span>
                <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:5,background:ac+"22",color:ac}}>⚽ Matches {m.matches}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{...C,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"90px 1fr 1fr 1.4fr",gap:8,padding:"8px 12px",borderBottom:`1px solid ${AS.borderColor}`,fontSize:9,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>
            <span>Date</span><span>Session</span><span>Team/Squad</span><span>Coaches</span>
          </div>
          <div style={{maxHeight:360,overflowY:"auto"}}>
            {visibleSessionLog.length===0?(
              <div style={{padding:24,textAlign:"center",color:"var(--c-muted2)",fontSize:11}}>No sessions for this selection.</div>
            ):visibleSessionLog.map(s=>(
              <div key={s._kind+s.id} style={{display:"grid",gridTemplateColumns:"90px 1fr 1fr 1.4fr",gap:8,padding:"8px 12px",borderTop:`1px solid ${AS.borderColor}`,fontSize:11,alignItems:"center"}}>
                <span style={{color:"var(--c-muted)"}}>{fmtDate(s.date)}</span>
                <span style={{fontWeight:600}}>{s.type==="game"?"⚽":"🏃"} {s.label}</span>
                <span style={{color:"var(--c-muted)"}}>{s._scopeName}</span>
                <span style={{color:"var(--c-muted)"}}>{(s.coach_ids||[]).length===0?"—":(s.coach_ids||[]).map(id=>coaches.find(c=>c.id===id)?.name||"?").join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      </>)}
    </div>
  </div>);
}

// ── OrgStatsView ─────────────────────────────────────────────────────────────
// Org-wide view (from Player Base) combining goals/assists across every team a
// player appears on, plus a single attendance rate blending team sessions AND
// training-group sessions.

export function OrgStatsView({allPlayers,teams,groups,orgAttendance,orgAttendanceRange,orgAttendanceRangeLoading,onLoadAttendanceRange,orgRatings,matches,ac,AS,featPlayerRatings=true,featMatchDay=false,matchTimeByFixture,onOpenPlayerStats,token,coachId,isMobile}){
  const [sortKey,setSortKey]=useState("goals");
  const [filter,setFilter]=useState("all"); // "all" | "team:<id>" | "group:<id>"
  const [genderFilter,setGenderFilter]=useState("all"); // "all" | "male" | "female"
  const [positionFilter,setPositionFilter]=useState("all"); // "all" | one of POSITIONS
  const [haTeamId,setHaTeamId]=useState("all"); // "all" (whole organisation) | team id
  const [attFinderGender,setAttFinderGender]=useState("all"); // "all" | "male" | "female"
  const [attFinderTeam,setAttFinderTeam]=useState("all"); // "all" | team id
  const [attFinderMetric,setAttFinderMetric]=useState("overall"); // "overall" | "match" | "training"
  const [attFinderOp,setAttFinderOp]=useState(">="); // ">" | "<" | ">=" | "<="
  const [attFinderPct,setAttFinderPct]=useState(50);
  const [attFinderStart,setAttFinderStart]=useState(""); // "" | "YYYY-MM-DD"
  const [attFinderEnd,setAttFinderEnd]=useState("");
  const [attFinderResetKey,setAttFinderResetKey]=useState(0); // bumped on Clear Filters to force every control to remount at its default value
  const [mtGenderFilter,setMtGenderFilter]=useState("all"); // Match Time section's own filters — independent of the Overall Player Stats table's filters above
  const [mtPositionFilter,setMtPositionFilter]=useState("all");
  const [mtTeamFilter,setMtTeamFilter]=useState("all"); // "all" | team id
  useEffect(()=>{
    if(onLoadAttendanceRange)onLoadAttendanceRange(attFinderStart||null,attFinderEnd||null);
  },[attFinderStart,attFinderEnd]);
  const [statsPick,setStatsPick]=useState(null); // player row clicked while on >1 team: {player, playerTeams}
  const [overallPlayer,setOverallPlayer]=useState(null); // player row whose combined-across-teams card is open
  // Which sections of this page are currently visible — defaults to "All" on every page load.
  const [orgStatsVisible,setOrgStatsVisible]=useStatsVisibility([{id:"playerStatsTable"},{id:"attendanceSummary"},{id:"attendanceFinder"},{id:"homeAway"},{id:"matchTime"}]);
  // Drag-to-reorder these same sections, persisted per coach — same pattern as
  // the per-team Stats page's ReorderableBlocks.
  const [orgStatsOrder,setOrgStatsOrder]=useState(ORG_STATS_DEFAULT_ORDER);
  useEffect(()=>{ if(token&&coachId)loadBlockOrder(token,coachId,"coach_org_stats",ORG_STATS_DEFAULT_ORDER).then(setOrgStatsOrder); },[token,coachId]);
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  function pct(bucket){ return bucket&&bucket.total>0?Math.round(((bucket.present+bucket.late)/bucket.total)*100):null; }
  const avgOf=arr=>arr&&arr.length>0?Math.round((arr.reduce((a,b)=>a+b,0)/arr.length)*10)/10:null;
  const avgPct=arr=>arr.length>0?Math.round(arr.reduce((a,b)=>a+b,0)/arr.length):null;
  // Averages each player's individual attendance % (not a pooled present/total),
  // matching the same per-player rates shown in the table above — a team of two
  // players at 100% and 50% shows as 75% here either way, but this stays
  // consistent with how each player's own row is calculated.
  function attendanceSummaryFor(players,orgAttendance){
    const training=[],match=[],overall=[];
    players.forEach(p=>{
      const att=orgAttendance?.[p.id];
      if(!att)return;
      const tr=pct(att.byType?.training);
      const mr=pct(att.byType?.game);
      const or_=att.total>0?Math.round(((att.present+att.late)/att.total)*100):null;
      if(tr!==null)training.push(tr);
      if(mr!==null)match.push(mr);
      if(or_!==null)overall.push(or_);
    });
    return {training:avgPct(training),match:avgPct(match),overall:avgPct(overall)};
  }
  // Filters apply in this order, matching the dropdowns left-to-right: Gender, then
  // Position, then Team/Training Group.
  let filteredPlayers=allPlayers;
  if(genderFilter!=="all")filteredPlayers=filteredPlayers.filter(p=>p.gender===genderFilter);
  if(positionFilter!=="all")filteredPlayers=filteredPlayers.filter(p=>p.position===positionFilter);
  if(filter.startsWith("team:")){
    const t=teams.find(t=>t.id===filter.slice(5));
    filteredPlayers=t?filteredPlayers.filter(p=>(t.playerIds||[]).includes(p.id)):filteredPlayers;
  } else if(filter.startsWith("group:")){
    const g=(groups||[]).find(g=>g.id===filter.slice(6));
    filteredPlayers=g?filteredPlayers.filter(p=>(g.playerIds||[]).includes(p.id)):filteredPlayers;
  }
  const isGroupFilter=filter.startsWith("group:");
  const rows=filteredPlayers.map(p=>{
    let goals=0,assists=0;
    const coachRatings=[],selfRatings=[];
    teams.forEach(t=>{
      const s=t.playerStats[p.id];
      if(s){goals+=s.goals||0;assists+=s.assists||0;}
      if((t.playerIds||[]).includes(p.id)){
        const r=orgRatings?.[t.id]?.[p.id];
        if(r){coachRatings.push(...(r.coach||[]));selfRatings.push(...(r.player||[]));}
      }
    });
    const att=orgAttendance?.[p.id];
    const attended=att?att.present+att.late:0;
    const rate=att&&att.total>0?Math.round((attended/att.total)*100):null;
    const matchRate=pct(att?.byType?.game);
    const trainingRate=pct(att?.byType?.training);
    return {...p,goals,assists,attTotal:att?.total||0,rate,matchRate,trainingRate,coachAvg:avgOf(coachRatings),selfAvg:avgOf(selfRatings)};
  });
  const sorted=[...rows].sort((a,b)=>{
    if(sortKey==="rate")return (b.rate??-1)-(a.rate??-1);
    if(sortKey==="matchRate")return (b.matchRate??-1)-(a.matchRate??-1);
    if(sortKey==="trainingRate")return (b.trainingRate??-1)-(a.trainingRate??-1);
    if(sortKey==="coachAvg")return (b.coachAvg??-1)-(a.coachAvg??-1);
    if(sortKey==="selfAvg")return (b.selfAvg??-1)-(a.selfAvg??-1);
    if(sortKey==="position")return (a.position||"").localeCompare(b.position||"");
    return (b[sortKey]||0)-(a[sortKey]||0);
  });
  const barColor=r=>r>=80?"#22c55e":r>=50?"#eab308":"#ef4444";
  // Coach/Self Avg sit right after Goals/Assists (not tacked on at the far right after
  // three wide attendance-bar columns) so they're visible without having to scroll the
  // table all the way over — that's what made them easy to miss after the "Match Ratings
  // by Team" section was folded into this single table.
  // The name column used to be a flexible "1.1fr"/"1.2fr" track, which on a wide desktop
  // screen (especially now the app's main container is wider) stretched way past what a
  // name needs and pushed a big gap in front of the other columns. Capping it with
  // minmax keeps names readable without eating space the other columns could use, and
  // any leftover row width now just sits after the last column instead.
  // The last few columns (rating averages + the three attendance bars) use
  // minmax(_,1fr) rather than a fixed px width so they stretch to actually fill
  // the table's width on wide screens instead of leaving a blank gap after
  // Training Att. — only the name column and the short Pos/G/A columns stay
  // fixed-ish, since those don't benefit from extra width.
  const cols=featPlayerRatings?"minmax(150px,260px) 46px 28px 28px minmax(55px,1fr) minmax(55px,1fr) minmax(80px,1fr) minmax(80px,1fr) minmax(80px,1fr)":"minmax(150px,260px) 54px 34px 34px minmax(90px,1fr) minmax(90px,1fr) minmax(90px,1fr)";
  const haTeamOptions=genderFilter==="all"?teams:teams.filter(t=>t.gender===genderFilter);
  const filtersChanged=sortKey!=="goals"||filter!=="all"||genderFilter!=="all"||positionFilter!=="all";
  function resetFilters(){setSortKey("goals");setFilter("all");setGenderFilter("all");setPositionFilter("all");}
  const resetBtnStyle={padding:"5px 10px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:10,fontWeight:700,cursor:"pointer"};
  // Clicking a player row jumps to their individual stats page. A player on just one
  // team goes straight there; a player on several needs to say which team's stats page
  // to open (or see the numbers combined across every team, right here).
  function openPlayerStats(p){
    const playerTeams=teams.filter(t=>(t.playerIds||[]).includes(p.id));
    if(playerTeams.length===0)return;
    if(playerTeams.length===1){ onOpenPlayerStats&&onOpenPlayerStats(playerTeams[0].id,p.id); return; }
    setStatsPick({player:p,playerTeams});
  }
  // ── Attendance Finder ────────────────────────────────────────────────────
  // Independent of the filters on the table above — lets a coach ask
  // "who's below/above X% attendance", filtered by gender and/or team.
  let attFinderPlayers=allPlayers;
  if(attFinderGender!=="all")attFinderPlayers=attFinderPlayers.filter(p=>p.gender===attFinderGender);
  if(attFinderTeam!=="all"){
    const ft=teams.find(t=>t.id===attFinderTeam);
    attFinderPlayers=ft?attFinderPlayers.filter(p=>(ft.playerIds||[]).includes(p.id)):attFinderPlayers;
  }
  const attFinderDateActive=!!(attFinderStart&&attFinderEnd);
  const attFinderSource=attFinderDateActive?orgAttendanceRange:orgAttendance;
  const attFinderRows=attFinderPlayers.map(p=>{
    const att=attFinderSource?.[p.id];
    const rate=pct(att);
    const matchRate=pct(att?.byType?.game);
    const trainingRate=pct(att?.byType?.training);
    return {...p,rate,matchRate,trainingRate};
  });
  function attFinderValue(p){ return attFinderMetric==="match"?p.matchRate:attFinderMetric==="training"?p.trainingRate:p.rate; }
  function attFinderCompare(v){
    if(v===null||v===undefined)return false;
    if(attFinderOp===">")return v>attFinderPct;
    if(attFinderOp==="<")return v<attFinderPct;
    if(attFinderOp==="<=")return v<=attFinderPct;
    return v>=attFinderPct;
  }
  const attFinderResults=attFinderRows.filter(p=>attFinderCompare(attFinderValue(p))).sort((a,b)=>(attFinderValue(b)??-1)-(attFinderValue(a)??-1));
  const attFinderMetricLabel=attFinderMetric==="match"?"Match Attendance":attFinderMetric==="training"?"Training Attendance":"Overall Attendance";
  // Match Time section's own filters — independent of the Overall Player Stats table's filters above.
  let mtPlayers=allPlayers;
  if(mtGenderFilter!=="all")mtPlayers=mtPlayers.filter(p=>p.gender===mtGenderFilter);
  if(mtPositionFilter!=="all")mtPlayers=mtPlayers.filter(p=>p.position===mtPositionFilter);
  if(mtTeamFilter!=="all"){
    const mtt=teams.find(tm=>tm.id===mtTeamFilter);
    mtPlayers=mtt?mtPlayers.filter(p=>(mtt.playerIds||[]).includes(p.id)):mtPlayers;
  }
  const mtFixtures=mtTeamFilter!=="all"?(matches||[]).filter(f=>f.team_id===mtTeamFilter):matches;
  const mtFiltersChanged=mtGenderFilter!=="all"||mtPositionFilter!=="all"||mtTeamFilter!=="all";
  const orgStatOptions=[{id:"playerStatsTable",label:"Overall Player Stats"},{id:"attendanceSummary",label:"Attendance Summary"},{id:"attendanceFinder",label:"Attendance Finder"},{id:"homeAway",label:"Home vs Away"},...(featMatchDay?[{id:"matchTime",label:"Match Time"}]:[])];
  const orgBlockDefs=[
    {id:"playerStatsTable",node:(
      <div style={{...C,padding:16}}>
        <h3 style={{margin:"0 0 4px",fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Overall Player Stats</h3>
        <div style={{fontSize:11,color:"var(--c-muted)",lineHeight:1.5,marginBottom:12}}>Goals, assists, attendance, and match ratings for every player across your club, all in one table — filter by gender, position, or team/training group, and sort by any column. Click a player's row to open their individual stats page.</div>
        {!featPlayerRatings&&<div style={{fontSize:11,color:"#f59e0b",lineHeight:1.5,marginBottom:12}}>Coach/Self rating columns are hidden because the Player Ratings feature is switched off for your club — turn it back on from Settings → Features to see them here.</div>}
        <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:6}}>
            <select value={genderFilter} onChange={e=>setGenderFilter(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
              <option value="all">All Genders</option>
              <option value="male">♂ Male</option>
              <option value="female">♀ Female</option>
            </select>
            <select value={positionFilter} onChange={e=>setPositionFilter(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
              <option value="all">All Positions</option>
              {POSITIONS.map(pos=><option key={pos} value={pos}>{pos}</option>)}
            </select>
            <select value={filter} onChange={e=>setFilter(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
              <option value="all">All Players</option>
              {teams.length>0&&<optgroup label="Teams">{teams.map(t=><option key={t.id} value={"team:"+t.id}>{t.badge} {t.name}</option>)}</optgroup>}
              {groups&&groups.length>0&&<optgroup label="Training Groups">{groups.map(g=><option key={g.id} value={"group:"+g.id}>{g.badge||"🏋️"} {g.name}</option>)}</optgroup>}
            </select>
            <select value={sortKey} onChange={e=>setSortKey(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
              <option value="goals">Sort: Goals</option><option value="assists">Sort: Assists</option><option value="position">Sort: Position</option><option value="rate">Sort: Attendance</option><option value="matchRate">Sort: Match Attendance</option><option value="trainingRate">Sort: Training Attendance</option>
              {featPlayerRatings&&<option value="coachAvg">Sort: Coach Rating</option>}
              {featPlayerRatings&&<option value="selfAvg">Sort: Self Rating</option>}
            </select>
            <button onClick={resetFilters} disabled={!filtersChanged} title="Reset filters and sort back to defaults" style={{...resetBtnStyle,opacity:filtersChanged?1:0.45,cursor:filtersChanged?"pointer":"default"}}>↺ Reset</button>
        </div>
        {orgAttendance===null?<div style={{padding:30,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>Loading…</div>:filteredPlayers.length===0?<div style={{padding:30,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No players match these filters.</div>:(<>
        {isGroupFilter&&featPlayerRatings&&(
          <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic",marginBottom:8}}>Match ratings are tracked per team, not per training group, so Coach/Self Avg won't show for a training-group selection.</div>
        )}
        <div style={{overflowX:"auto"}}>
        <div style={{minWidth:featPlayerRatings?600:480,width:"100%"}}>
        <div style={{display:"grid",gridTemplateColumns:cols,gap:6,padding:"4px 5px",fontSize:8,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",borderBottom:`1px solid ${AS.borderColor}`,marginBottom:3}}>
          <span>Player</span><span style={{textAlign:"center"}}>Pos</span><span title="Goals" style={{textAlign:"center"}}>G</span><span title="Assists" style={{textAlign:"center"}}>A</span>
          {featPlayerRatings&&<span title="Coach rating average" style={{textAlign:"center"}}>Coach</span>}
          {featPlayerRatings&&<span title="Self rating average" style={{textAlign:"center"}}>Self</span>}
          <span style={{textAlign:"center"}}>Attendance</span><span style={{textAlign:"center"}}>Match Att.</span><span style={{textAlign:"center"}}>Training Att.</span>
        </div>
        {sorted.map(p=>(
          <div key={p.id} onClick={()=>openPlayerStats(p)} title="View this player's stats page" style={{display:"grid",gridTemplateColumns:cols,gap:6,padding:"7px 5px",alignItems:"center",borderBottom:`1px solid ${AS.bgColor}`,cursor:"pointer"}}>
            <span style={{fontSize:11,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><Flag value={p.nationality}/> {p.name}</span>
            <span style={{textAlign:"center",fontSize:10,fontWeight:700,color:"var(--c-muted)"}}>{p.position||"—"}</span>
            <span style={{textAlign:"center",fontWeight:700,fontSize:11}}>{p.goals}</span>
            <span style={{textAlign:"center",fontWeight:700,fontSize:11,color:"var(--c-text2)"}}>{p.assists}</span>
            {featPlayerRatings&&<span style={{textAlign:"center",fontWeight:700,fontSize:11,color:p.coachAvg!==null?ac:"var(--c-muted2)"}}>{p.coachAvg!==null?`${p.coachAvg}`:"—"}</span>}
            {featPlayerRatings&&<span style={{textAlign:"center",fontSize:11,color:"var(--c-muted)"}}>{p.selfAvg!==null?`${p.selfAvg}`:"—"}</span>}
            {[p.rate,p.matchRate,p.trainingRate].map((r,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:4}}>
                <div style={{flex:1,background:AS.bgColor,borderRadius:2,height:4,overflow:"hidden"}}><div style={{width:`${r??0}%`,background:barColor(r??0),height:"100%",borderRadius:2}}/></div>
                <span style={{fontSize:9,fontWeight:700,minWidth:28,textAlign:"right",color:r===null?"var(--c-muted2)":"var(--c-text2)"}}>{r!==null?`${r}%`:"—"}</span>
              </div>
            ))}
          </div>
        ))}
        </div>
        </div>
        </>)}
      </div>
    )},
    {id:"attendanceSummary",node:(
      <div style={{...C,padding:16}}>
        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>📋 Attendance Summary</div>
        <div style={{fontSize:11,color:"var(--c-muted)",lineHeight:1.5,marginBottom:12}}>Average training, match, and overall attendance — one card per team, plus a breakdown by gender across the whole club.</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:10}}>
          {teams.map(t=>{
            const teamPlayers=(t.playerIds||[]).map(pid=>allPlayers.find(p=>p.id===pid)).filter(Boolean);
            const a=attendanceSummaryFor(teamPlayers,orgAttendance);
            return(
              <div key={t.id} style={{background:AS.bgColor,borderRadius:9,padding:14}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
                  <span style={{fontSize:14}}>{t.badge}</span><span style={{fontSize:12,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</span>
                </div>
                {[["Training",a.training],["Match",a.match],["Overall",a.overall]].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"3px 0"}}>
                    <span style={{fontSize:10,color:"var(--c-muted)"}}>{l}</span>
                    <span style={{fontSize:12,fontWeight:800,color:v===null?"var(--c-muted2)":barColor(v)}}>{v!==null?`${v}%`:"—"}</span>
                  </div>
                ))}
              </div>
            );
          })}
          {teams.length===0&&<div style={{background:AS.bgColor,borderRadius:9,padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12,gridColumn:"1/-1"}}>No teams yet.</div>}
          {[["male","♂ Male"],["female","♀ Female"]].map(([g,label])=>{
            const genderPlayers=allPlayers.filter(p=>p.gender===g);
            const a=attendanceSummaryFor(genderPlayers,orgAttendance);
            return(
              <div key={g} style={{background:AS.bgColor,borderRadius:9,padding:14}}>
                <div style={{fontSize:12,fontWeight:700,marginBottom:10}}>{label}</div>
                {[["Training",a.training],["Match",a.match],["Overall",a.overall]].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"3px 0"}}>
                    <span style={{fontSize:10,color:"var(--c-muted)"}}>{l}</span>
                    <span style={{fontSize:12,fontWeight:800,color:v===null?"var(--c-muted2)":barColor(v)}}>{v!==null?`${v}%`:"—"}</span>
                  </div>
                ))}
                {genderPlayers.length===0&&<div style={{fontSize:10,color:"var(--c-muted2)",marginTop:4}}>No players recorded as {label.slice(2).toLowerCase()}.</div>}
              </div>
            );
          })}
        </div>
      </div>
    )},
    {id:"attendanceFinder",node:(
      <div style={{...C,padding:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4,flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>🔍 Attendance Finder</div>
        </div>
        <div style={{fontSize:11,color:"var(--c-muted)",lineHeight:1.5,marginBottom:10}}>Find every player whose attendance meets a chosen threshold — e.g. everyone under 50% training attendance, or girls' teams above 80% overall. Optionally narrow it to a specific date range.</div>
        <div key={attFinderResetKey} style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:6,marginBottom:8}}>
          <select defaultValue={attFinderGender} onChange={e=>setAttFinderGender(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
            <option value="all">All Genders</option>
            <option value="male">♂ Male</option>
            <option value="female">♀ Female</option>
          </select>
          <select defaultValue={attFinderTeam} onChange={e=>setAttFinderTeam(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
            <option value="all">All Teams</option>
            {teams.map(t=><option key={t.id} value={t.id}>{t.badge} {t.name}</option>)}
          </select>
          <select defaultValue={attFinderMetric} onChange={e=>setAttFinderMetric(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
            <option value="overall">Overall Attendance</option>
            <option value="match">Match Attendance</option>
            <option value="training">Training Attendance</option>
          </select>
          <select defaultValue={attFinderOp} onChange={e=>setAttFinderOp(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
            <option value=">">{"> greater than"}</option>
            <option value=">=">{"≥ at least"}</option>
            <option value="<">{"< less than"}</option>
            <option value="<=">{"≤ at most"}</option>
          </select>
          <input type="number" min={0} max={100} defaultValue={attFinderPct} onChange={e=>setAttFinderPct(Math.max(0,Math.min(100,Number(e.target.value)||0)))} style={{width:56,padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10}}/>
          <span style={{fontSize:10,color:"var(--c-muted)"}}>%</span>
          <button onClick={()=>{setAttFinderGender("all");setAttFinderTeam("all");setAttFinderMetric("overall");setAttFinderOp(">=");setAttFinderPct(50);setAttFinderStart("");setAttFinderEnd("");setAttFinderResetKey(k=>k+1);}} title="Clear all Attendance Finder filters" style={resetBtnStyle}>✕ Clear Filters</button>
        </div>
        <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:6,marginBottom:12}}>
          <span style={{fontSize:10,color:"var(--c-muted)"}}>Between</span>
          <input key={`s${attFinderResetKey}`} type="date" defaultValue={attFinderStart} max={attFinderEnd||undefined} onChange={e=>setAttFinderStart(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10}}/>
          <span style={{fontSize:10,color:"var(--c-muted)"}}>and</span>
          <input key={`e${attFinderResetKey}`} type="date" defaultValue={attFinderEnd} min={attFinderStart||undefined} onChange={e=>setAttFinderEnd(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10}}/>
          {(attFinderStart||attFinderEnd)&&<button onClick={()=>{setAttFinderStart("");setAttFinderEnd("");setAttFinderResetKey(k=>k+1);}} title="Clear date range" style={resetBtnStyle}>✕ Clear dates</button>}
          {attFinderStart&&!attFinderEnd&&<span style={{fontSize:10,color:"var(--c-muted2)",fontStyle:"italic"}}>Pick an end date too</span>}
          {!attFinderStart&&attFinderEnd&&<span style={{fontSize:10,color:"var(--c-muted2)",fontStyle:"italic"}}>Pick a start date too</span>}
        </div>
        {attFinderDateActive&&orgAttendanceRangeLoading?(
          <div style={{background:AS.bgColor,borderRadius:9,padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>Loading attendance for this date range…</div>
        ):attFinderResults.length===0?(
          <div style={{background:AS.bgColor,borderRadius:9,padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No players match this criteria.</div>
        ):(
          <div style={{background:AS.bgColor,borderRadius:9,padding:8}}>
            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",padding:"4px 6px 8px",textTransform:"uppercase",letterSpacing:"0.05em"}}>{attFinderResults.length} player{attFinderResults.length===1?"":"s"} {attFinderOp} {attFinderPct}% {attFinderMetricLabel}{attFinderDateActive?` (${fmtDate(attFinderStart)} – ${fmtDate(attFinderEnd)})`:""}</div>
            {attFinderResults.map(p=>{
              const v=attFinderValue(p);
              return(
                <div key={p.id} onClick={()=>openPlayerStats(p)} title="View this player's stats page" style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 6px",borderBottom:`1px solid ${AS.surfaceColor}`,cursor:"pointer"}}>
                  <span style={{fontSize:11,fontWeight:600}}><Flag value={p.nationality}/> {p.name}</span>
                  <span style={{fontSize:11,fontWeight:800,color:v===null?"var(--c-muted2)":barColor(v)}}>{v!==null?`${v}%`:"—"}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    )},
    {id:"homeAway",node:(
      <div style={{...C,padding:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4,flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>🏠 Home vs Away</div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <select value={haTeamOptions.some(t=>t.id===haTeamId)?haTeamId:"all"} onChange={e=>setHaTeamId(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
              <option value="all">🏆 {genderFilter==="all"?"Whole Organisation (all teams)":`All ${genderFilter==="female"?"Girls'":"Boys'"} Teams`}</option>
              {haTeamOptions.length>0&&<optgroup label="Teams">{haTeamOptions.map(t=><option key={t.id} value={t.id}>{t.badge} {t.name}</option>)}</optgroup>}
            </select>
            {haTeamId!=="all"&&<button onClick={()=>setHaTeamId("all")} title="Reset to whole organisation" style={{padding:"5px 10px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:10,fontWeight:700,cursor:"pointer"}}>↺ Reset</button>}
          </div>
        </div>
        <div style={{fontSize:11,color:"var(--c-muted)",lineHeight:1.5,marginBottom:8}}>How results split between home and away matches, per stat — pick a single team above or leave it on the whole organisation.</div>
        <HomeAwaySplitCard
          title={haTeamId==="all"||!haTeamOptions.some(t=>t.id===haTeamId)?(genderFilter==="all"?"🏆 All Teams Combined":`🏆 All ${genderFilter==="female"?"Girls'":"Boys'"} Teams Combined`):`${teams.find(t=>t.id===haTeamId)?.badge||""} ${teams.find(t=>t.id===haTeamId)?.name||""}`}
          matches={(matches||[]).filter(m=>!m.matches&&(haTeamOptions.some(t=>t.id===haTeamId)?m.team_id===haTeamId:haTeamOptions.some(t=>t.id===m.team_id)))}
          AS={AS}
        />
      </div>
    )},
    {id:"matchTime",node:(
      <div style={{...C,padding:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>🏟️ Match Time</div>
          <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:6}}>
            <select value={mtTeamFilter} onChange={e=>setMtTeamFilter(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
              <option value="all">All Teams</option>
              {teams.map(t=><option key={t.id} value={t.id}>{t.badge} {t.name}</option>)}
            </select>
            <select value={mtGenderFilter} onChange={e=>setMtGenderFilter(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
              <option value="all">All Genders</option>
              <option value="male">♂ Male</option>
              <option value="female">♀ Female</option>
            </select>
            <select value={mtPositionFilter} onChange={e=>setMtPositionFilter(e.target.value)} style={{padding:"5px 8px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:10,cursor:"pointer"}}>
              <option value="all">All Positions</option>
              {POSITIONS.map(p=><option key={p} value={p}>{p}</option>)}
            </select>
            {mtFiltersChanged&&<button onClick={()=>{setMtGenderFilter("all");setMtPositionFilter("all");setMtTeamFilter("all");}} style={resetBtnStyle}>↺ Reset</button>}
          </div>
        </div>
        <MatchTimeTable players={mtPlayers} fixtures={mtFixtures} matchTimeByFixture={matchTimeByFixture} ac={ac} AS={AS}/>
      </div>
    )},
  ];
  const featuredOrgBlockDefs=orgBlockDefs.filter(b=>b.id!=="matchTime"||featMatchDay);
  const visibleOrgBlockDefs=featuredOrgBlockDefs.filter(b=>orgStatsVisible.has(b.id));
  return(<div style={{display:"flex",flexDirection:"column",gap:16}}>
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
    <div>
      <div style={{fontWeight:700,fontSize:17}}>📊 Overall Stats</div>
      <div style={{fontSize:11,color:"var(--c-muted)",marginTop:2,maxWidth:560}}>A club-wide view of every team combined — goals, assists, attendance, ratings, and match time across your whole organisation. Drag any section below to reorder it, or use the menu to hide ones you don't need.</div>
    </div>
    <StatsFilterMenu options={orgStatOptions} visible={orgStatsVisible} setVisible={setOrgStatsVisible} AS={AS}/>
  </div>
  <ReorderableBlocks blocks={visibleOrgBlockDefs} order={orgStatsOrder} onOrderChange={newOrder=>{setOrgStatsOrder(newOrder);if(token&&coachId)saveBlockOrder(token,coachId,"coach_org_stats",newOrder);}} showHandle={!isMobile}/>
  {statsPick&&(
    <div onClick={()=>setStatsPick(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{...C,padding:20,maxWidth:360,width:"100%"}}>
        <div style={{fontSize:14,fontWeight:800,marginBottom:2}}>{statsPick.player.name}</div>
        <div style={{fontSize:11,color:"var(--c-muted)",marginBottom:14}}>This player is on more than one team — which stats page do you want to open?</div>
        <div style={{display:"flex",flexDirection:"column",gap:7}}>
          {statsPick.playerTeams.map(t=>(
            <button key={t.id} onClick={()=>{onOpenPlayerStats&&onOpenPlayerStats(t.id,statsPick.player.id);setStatsPick(null);}} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:12,fontWeight:600,cursor:"pointer",textAlign:"left"}}>
              <span>{t.badge}</span><span>{t.name}</span>
            </button>
          ))}
          <button onClick={()=>{setOverallPlayer(statsPick.player);setStatsPick(null);}} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",borderRadius:8,border:"none",background:ac,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",textAlign:"left"}}>
            <span>🌐</span><span>Overall — combined across teams</span>
          </button>
        </div>
        <button onClick={()=>setStatsPick(null)} style={{marginTop:14,background:"none",border:"none",color:"var(--c-muted)",fontSize:11,cursor:"pointer",padding:0}}>Cancel</button>
      </div>
    </div>
  )}
  {overallPlayer&&(()=>{
    const p=rows.find(r=>r.id===overallPlayer.id)||overallPlayer;
    const playerTeams=teams.filter(t=>(t.playerIds||[]).includes(p.id));
    return(
      <div onClick={()=>setOverallPlayer(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16}}>
        <div onClick={e=>e.stopPropagation()} style={{...C,padding:20,maxWidth:380,width:"100%"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
            <span style={{fontSize:15,fontWeight:800}}><Flag value={p.nationality}/> {p.name}</span>
          </div>
          <div style={{fontSize:11,color:"var(--c-muted)",marginBottom:14}}>{p.position||"—"} · Combined across {playerTeams.map(t=>t.name).join(" & ")||"all teams"}</div>
          <div style={{display:"flex",gap:8,marginBottom:14}}>
            <div style={{flex:1,textAlign:"center",padding:"10px 6px",borderRadius:8,background:AS.bgColor}}><div style={{fontSize:9,color:"var(--c-text)",textTransform:"uppercase",fontWeight:700}}>Goals</div><div style={{fontSize:18,fontWeight:800,marginTop:2}}>{p.goals}</div></div>
            <div style={{flex:1,textAlign:"center",padding:"10px 6px",borderRadius:8,background:AS.bgColor}}><div style={{fontSize:9,color:"var(--c-text)",textTransform:"uppercase",fontWeight:700}}>Assists</div><div style={{fontSize:18,fontWeight:800,marginTop:2}}>{p.assists}</div></div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:featPlayerRatings?14:0}}>
            {[["Overall Attendance",p.rate],["Match Attendance",p.matchRate],["Training Attendance",p.trainingRate]].map(([l,r])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:11,color:"var(--c-muted)"}}>{l}</span>
                <span style={{fontSize:12,fontWeight:800,color:r===null?"var(--c-muted2)":barColor(r)}}>{r!==null?`${r}%`:"—"}</span>
              </div>
            ))}
          </div>
          {featPlayerRatings&&(
            <div style={{display:"flex",gap:8}}>
              <div style={{flex:1,textAlign:"center",padding:"8px 6px",borderRadius:8,background:AS.bgColor}}><div style={{fontSize:9,color:"var(--c-text)",textTransform:"uppercase",fontWeight:700}}>Coach Avg</div><div style={{fontSize:14,fontWeight:800,marginTop:2,color:p.coachAvg!==null?ac:"var(--c-text)"}}>{p.coachAvg!==null?`${p.coachAvg}/10`:"—"}</div></div>
              <div style={{flex:1,textAlign:"center",padding:"8px 6px",borderRadius:8,background:AS.bgColor}}><div style={{fontSize:9,color:"var(--c-text)",textTransform:"uppercase",fontWeight:700}}>Self Avg</div><div style={{fontSize:14,fontWeight:800,marginTop:2}}>{p.selfAvg!==null?`${p.selfAvg}/10`:"—"}</div></div>
            </div>
          )}
          <button onClick={()=>setOverallPlayer(null)} style={{marginTop:16,width:"100%",padding:"9px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:12,fontWeight:700,cursor:"pointer"}}>Close</button>
        </div>
      </div>
    );
  })()}
  </div>);
}

// ── InboxView ────────────────────────────────────────────────────────────────
// Coach-facing list of every player-notes thread, sorted most-recently-active first.

export function InboxView({threads,pendingLinks,onApproveLink,onRejectLink,onOpen,ac,AS}){
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  return(<div style={{display:"flex",flexDirection:"column",gap:14}}>
    {pendingLinks&&pendingLinks.length>0&&(
      <div style={{...C,padding:12,border:"1px solid #f59e0b55",background:"#7c2d1220"}}>
        <div style={{fontSize:11,fontWeight:700,color:"#f59e0b",marginBottom:8}}>⏳ Player account link requests ({pendingLinks.length})</div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {pendingLinks.map(l=>(
            <div key={l.id} style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",padding:"7px 9px",background:AS.bgColor,borderRadius:7}}>
              <span style={{fontSize:12,flex:1,minWidth:180}}>
                <b>{l.name}</b> <span style={{color:"var(--c-muted)"}}>says they're</span> <b>{l.playerName}</b>
                {l.teamInfo&&<span style={{color:"var(--c-muted2)"}}> · {l.teamInfo.badge} {l.teamInfo.name}</span>}
              </span>
              <button onClick={()=>onApproveLink(l.id)} style={{padding:"5px 11px",borderRadius:6,border:"none",background:"#22c55e",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}}>Approve</button>
              <button onClick={()=>onRejectLink(l.id)} style={{padding:"5px 11px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:11,cursor:"pointer"}}>Reject</button>
            </div>
          ))}
        </div>
      </div>
    )}
    {threads===null?(
      <div style={{...C,padding:36,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>Loading inbox…</div>
    ):threads.length===0?(
      (!pendingLinks||pendingLinks.length===0)&&<div style={{...C,padding:36,textAlign:"center",color:"var(--c-muted2)"}}><div style={{fontSize:26,marginBottom:7}}>📥</div><div style={{fontSize:12,fontWeight:600,color:"var(--c-muted)"}}>No notes sent yet</div><div style={{fontSize:11,marginTop:4}}>Open a player's profile to send them a note.</div></div>
    ):(
    <div style={{...C,padding:8}}>
    {threads.map(t=>(
      <div key={t.id} onClick={()=>t.player&&onOpen(t.player)} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 10px",borderRadius:8,cursor:"pointer",borderBottom:`1px solid ${AS.bgColor}`}} onMouseEnter={e=>e.currentTarget.style.background=AS.bgColor} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
        <div style={{width:36,height:36,borderRadius:"50%",background:ac+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0,fontWeight:700,color:ac}}>{(t.player?.name||"?").charAt(0)}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,fontWeight:t.unread>0?800:600}}><Flag value={t.player?.nationality}/> {t.player?.name||"Unknown player"}</div>
          <div style={{fontSize:11,color:"var(--c-muted)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.lastMsg?(t.lastMsg.sender_type==="coach"?"You: ":"")+t.lastMsg.body:"No messages yet"}</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
          <span style={{fontSize:9,color:"var(--c-muted2)"}}>{t.lastMsg?new Date(t.lastMsg.created_at).toLocaleDateString([],{month:"short",day:"numeric"}):""}</span>
          {t.unread>0&&<span style={{fontSize:9,fontWeight:800,background:"#ef4444",color:"#fff",borderRadius:8,padding:"1px 6px"}}>{t.unread}</span>}
        </div>
      </div>
    ))}
    </div>
    )}
  </div>);
}

// ── UsersView ────────────────────────────────────────────────────────────────
// Coach-facing list of every player account (team_viewers) across the org, with
// login stats and, for head coaches/admins, the ability to fix up their player link.

export function UsersView({users,allPlayers,teams,isHead,onUpdateLink,ac,AS}){
  const [editingId,setEditingId]=useState(null);
  const [editPid,setEditPid]=useState("");
  const [resetSentId,setResetSentId]=useState(null);
  const [resettingId,setResettingId]=useState(null);
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};

  async function sendPasswordReset(u){
    setResettingId(u.id);
    const res=await api.recover(u.email);
    setResettingId(null);
    if(res?.error){alert(res.error.message||"Couldn't send reset email — please try again.");return;}
    setResetSentId(u.id);
    setTimeout(()=>setResetSentId(id=>id===u.id?null:id),4000);
  }

  if(users===null)return <div style={{...C,padding:36,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>Loading users…</div>;
  if(users.length===0)return <div style={{...C,padding:36,textAlign:"center",color:"var(--c-muted2)"}}><div style={{fontSize:26,marginBottom:7}}>👥</div><div style={{fontSize:12,fontWeight:600,color:"var(--c-muted)"}}>No player accounts yet</div><div style={{fontSize:11,marginTop:4}}>They'll show up here once players register with a join code.</div></div>;

  const byRecency=(a,b)=>{
    if(!a.last_login_at&&!b.last_login_at)return a.name.localeCompare(b.name);
    if(!a.last_login_at)return 1;
    if(!b.last_login_at)return -1;
    return new Date(b.last_login_at)-new Date(a.last_login_at);
  };
  const playerUsers=users.filter(u=>u.account_role!=="parent").sort(byRecency);
  const parentUsers=users.filter(u=>u.account_role==="parent").sort(byRecency);

  function startEdit(u){ setEditingId(u.id); setEditPid(u.player_id||""); }
  function saveEdit(u){
    onUpdateLink(u.id,{player_id:editPid||null,player_link_approved:!!editPid});
    setEditingId(null);
  }

  function renderUserRow(u){
    const team=teams.find(t=>t.id===u.team_id);
    const teamRoster=team?team.playerIds.map(pid=>allPlayers.find(p=>p.id===pid)).filter(Boolean):[];
    const isEditing=editingId===u.id;
    const isParent=u.account_role==="parent";
    return(
      <div key={u.id} style={{borderTop:`1px solid ${AS.borderColor}`,padding:"11px 14px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <div style={{width:32,height:32,borderRadius:"50%",background:(isParent?"#8b5cf6":ac)+"33",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,color:isParent?"#8b5cf6":ac,flexShrink:0}}>{(u.name||"?").charAt(0).toUpperCase()}</div>
          <div style={{flex:1,minWidth:140}}>
            <div style={{fontWeight:600,fontSize:12}}>{u.name}</div>
            <div style={{fontSize:10,color:"var(--c-muted)"}}>{u.email}{team&&!isParent&&<> · {team.badge} {team.name}</>}</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:11,fontWeight:600}}>
              {isParent?(
                (u.childrenNames&&u.childrenNames.length>0)?(<>👪 {u.childrenNames.join(", ")}</>):<span style={{color:"var(--c-muted2)",fontStyle:"italic"}}>No children linked</span>
              ):(
                u.player_id?(<>{u.playerName||"Unknown player"} {!u.player_link_approved&&<span style={{color:"#f59e0b",fontWeight:700}}>(pending)</span>}</>):<span style={{color:"var(--c-muted2)",fontStyle:"italic"}}>Not linked</span>
              )}
            </div>
            <div style={{fontSize:9,color:"var(--c-muted)",marginTop:2}}>{u.login_count||0} login{u.login_count===1?"":"s"} · Last: {u.last_login_at?new Date(u.last_login_at).toLocaleString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}):"Never"}</div>
          </div>
          <button onClick={()=>sendPasswordReset(u)} disabled={resettingId===u.id} title="Send a password reset link to this user's email" style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:resetSentId===u.id?"#10b98122":"none",color:resetSentId===u.id?"#10b981":"var(--c-muted)",fontSize:10,fontWeight:resetSentId===u.id?700:400,cursor:"pointer",flexShrink:0}}>{resetSentId===u.id?"✓ Sent":resettingId===u.id?"Sending…":"Reset Password"}</button>
          {isHead&&!isParent&&<button onClick={()=>isEditing?setEditingId(null):startEdit(u)} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:10,cursor:"pointer",flexShrink:0}}>{isEditing?"Cancel":"Edit"}</button>}
        </div>
        {isEditing&&(
          <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${AS.borderColor}`}}>
            <label style={{display:"block",fontSize:10,fontWeight:600,color:"var(--c-text)",marginBottom:5,textTransform:"uppercase"}}>Linked Player</label>
            <select value={editPid} onChange={e=>setEditPid(e.target.value)} style={{width:"100%",padding:"7px 9px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:12,marginBottom:8,cursor:"pointer"}}>
              <option value="">— Not linked —</option>
              {teamRoster.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div style={{fontSize:10,color:"var(--c-muted2)",marginBottom:8}}>Selecting a player here approves the link immediately — no separate approval step needed.</div>
            <button onClick={()=>saveEdit(u)} style={{padding:"6px 14px",borderRadius:6,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>Save</button>
          </div>
        )}
      </div>
    );
  }

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{...C,overflow:"hidden"}}>
        <div style={{padding:"8px 12px",borderBottom:`1px solid ${AS.borderColor}`,fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>⚽ Players ({playerUsers.length})</div>
        {playerUsers.length===0?<div style={{padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No player accounts yet.</div>:playerUsers.map(renderUserRow)}
      </div>
      <div style={{...C,overflow:"hidden"}}>
        <div style={{padding:"8px 12px",borderBottom:`1px solid ${AS.borderColor}`,fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>👪 Parents ({parentUsers.length})</div>
        {parentUsers.length===0?<div style={{padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No parent accounts yet.</div>:parentUsers.map(renderUserRow)}
      </div>
    </div>
  );
}

// ── CreateTeamModal ───────────────────────────────────────────────────────────
// The "clever bit": coach picks gender + age category, team is created,
// and every eligible player from the org player base is auto-added.
// ── League helper functions (pure, no side effects) ─────────────────────────

export function CreateTeamModal({org, allPlayers, onClose, onCreate, ac, isMobile, mode}){
  const [step,setStep]=useState(1); // 1: gender, 2: age category, 3: review & name
  const [gender,setGender]=useState(null);
  const [ageCategory,setAgeCategory]=useState(null);
  const [cutoffDate,setCutoffDate]=useState(()=>{
    // Default to Aug 1st of the season currently in progress (most common youth football rule)
    const now=new Date();
    const seasonStartYear = now.getMonth()>=7 ? now.getFullYear() : now.getFullYear()-1;
    return `${seasonStartYear}-08-01`;
  });
  const [teamName,setTeamName]=useState("");
  const [badge,setBadge]=useState("⭐");
  const [color,setColor]=useState("#3b82f6");
  const [loading,setLoading]=useState(false);
  const [excludedIds,setExcludedIds]=useState(()=>new Set());

  // All players automatically matched by gender + age category
  const autoEligiblePlayers = (gender&&ageCategory)
    ? allPlayers.filter(p=>p.gender===gender && isEligible(p.dob, ageCategory, cutoffDate))
    : [];
  // The final list after the coach has removed anyone they don't want
  const eligiblePlayers = autoEligiblePlayers.filter(p=>!excludedIds.has(p.id));

  function removeFromEligible(playerId){
    setExcludedIds(prev=>{const next=new Set(prev);next.add(playerId);return next;});
  }
  function restoreToEligible(playerId){
    setExcludedIds(prev=>{const next=new Set(prev);next.delete(playerId);return next;});
  }

  // Reset exclusions whenever the gender/age category/cutoff date selection changes
  useEffect(()=>{ setExcludedIds(new Set()); },[gender,ageCategory,cutoffDate]);

  useEffect(()=>{
    if(gender&&ageCategory&&!teamName){
      setTeamName(`${ageCategory} ${gender==="female"?"Girls":"Boys"}`);
    }
  },[gender,ageCategory]);

  const inp={width:"100%",padding:"9px 11px",borderRadius:7,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:13,outline:"none",boxSizing:"border-box"};
  const lbl={display:"block",fontSize:10,fontWeight:600,color:"var(--c-text)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em"};

  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,bottom:isMobile?"var(--nav-h,65px)":0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:100,padding:isMobile?0:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--c-surface)",borderRadius:isMobile?"16px 16px 0 0":14,width:"100%",maxWidth:isMobile?"100%":460,maxHeight:isMobile?"90vh":"85vh",border:"1px solid var(--c-border)",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 20px 12px",borderBottom:"1px solid var(--c-border)",flexShrink:0}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h2 style={{margin:0,fontSize:16,fontWeight:800}}>Create New Team</h2>
            <button onClick={onClose} style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:18}}>×</button>
          </div>
          <div style={{display:"flex",gap:4,marginTop:10}}>
            {[1,2,3].map(s=>(<div key={s} style={{flex:1,height:3,borderRadius:2,background:s<=step?ac:"var(--c-border)"}}/>))}
          </div>
        </div>

        <div style={{padding:20,flex:1,overflowY:"auto"}}>
          {/* Step 1: Gender */}
          {step===1&&(
            <div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--c-text2)",marginBottom:14}}>Step 1 — Which gender is this team for?</div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {[["male","♂ Boys / Male"],["female","♀ Girls / Female"]].map(([v,l])=>(
                  <button key={v} onClick={()=>{setGender(v);setStep(2);}} style={{padding:"16px",borderRadius:10,cursor:"pointer",fontSize:15,fontWeight:700,
                    background:"var(--c-bg0)",border:"1px solid var(--c-border)",color:"var(--c-text3)",textAlign:"left",transition:"all 0.15s"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=ac} onMouseLeave={e=>e.currentTarget.style.borderColor="var(--c-border)"}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Age category */}
          {step===2&&(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                <button onClick={()=>setStep(1)} style={{background:"none",border:"1px solid var(--c-border)",color:"var(--c-muted)",cursor:"pointer",fontSize:11,padding:"5px 9px",borderRadius:6}}>← Back</button>
                <div style={{fontSize:13,fontWeight:600,color:"var(--c-text2)"}}>Step 2 — Age category for {gender==="female"?"Girls":"Boys"}</div>
              </div>
              <div style={{marginBottom:14}}>
                <label style={lbl}>Age Calculated As Of</label>
                <input type="date" value={cutoffDate} onChange={e=>setCutoffDate(e.target.value)} style={{...inp,colorScheme:mode,maxWidth:200}}/>
                <div style={{display:"flex",gap:6,marginTop:7,flexWrap:"wrap"}}>
                  {(()=>{
                    const now=new Date();
                    const thisYear=now.getFullYear();
                    // Football seasons typically run Aug → Jul. If we're before Aug 1st,
                    // the "current season" started LAST calendar year, not this one —
                    // so both the Aug 1st and Dec 31st cutoffs for the current season
                    // fall in that earlier year.
                    const seasonStartYear = now.getMonth()>=7 ? thisYear : thisYear-1; // month 7 = August (0-indexed)
                    return [
                      ["Today", today()],
                      [`Aug 1, ${seasonStartYear} (this season)`, `${seasonStartYear}-08-01`],
                      [`Dec 31, ${seasonStartYear} (this season)`, `${seasonStartYear}-12-31`],
                    ];
                  })().map(([label,val])=>(
                    <button key={label} onClick={()=>setCutoffDate(val)} style={{padding:"4px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:600,
                      background:cutoffDate===val?ac+"22":"var(--c-bg0)",border:cutoffDate===val?`1px solid ${ac}`:"1px solid var(--c-border)",color:cutoffDate===val?ac:"var(--c-muted)"}}>{label}</button>
                  ))}
                </div>
                <div style={{fontSize:10,color:"var(--c-muted2)",marginTop:6}}>
                  Players' ages are calculated as of this date when matching them to an age category. For most UK youth leagues, use Aug 1st of the year the current season <em>started</em> (e.g. for the 2025/26 season, that's Aug 1, 2025) — a player who was 14 on that date is eligible for U15.
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(3,1fr)":"repeat(4,1fr)",gap:7}}>
                {AGE_CATEGORIES.map(cat=>{
                  const count=allPlayers.filter(p=>p.gender===gender && isEligible(p.dob,cat,cutoffDate)).length;
                  return(
                    <button key={cat} onClick={()=>{setAgeCategory(cat);setStep(3);}} style={{padding:"12px 6px",borderRadius:9,cursor:"pointer",
                      background:"var(--c-bg0)",border:"1px solid var(--c-border)",color:"var(--c-text3)",textAlign:"center",transition:"all 0.15s"}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor=ac} onMouseLeave={e=>e.currentTarget.style.borderColor="var(--c-border)"}>
                      <div style={{fontSize:15,fontWeight:800}}>{cat}</div>
                      <div style={{fontSize:9,color:count>0?ac:"var(--c-muted2)",marginTop:2}}>{count} eligible</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step===3&&(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                <button onClick={()=>setStep(2)} style={{background:"none",border:"1px solid var(--c-border)",color:"var(--c-muted)",cursor:"pointer",fontSize:11,padding:"5px 9px",borderRadius:6}}>← Back</button>
                <div style={{fontSize:13,fontWeight:600,color:"var(--c-text2)"}}>Step 3 — Confirm & name your team</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
                <div><label style={lbl}>Team Name</label><input value={teamName} onChange={e=>setTeamName(e.target.value)} style={inp}/></div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 80px",gap:9}}>
                  <div><label style={lbl}>Colour</label><div style={{display:"flex",flexWrap:"wrap",gap:6}}>{TEAM_COLORS.map(c=><button key={c} onClick={()=>setColor(c)} style={{width:24,height:24,borderRadius:"50%",background:c,border:color===c?"3px solid #fff":"3px solid transparent",cursor:"pointer"}}/>)}</div></div>
                  <div><label style={lbl}>Badge</label><IconPickerSelect value={badge} onChange={setBadge} AS={{borderColor:"var(--c-border)",bgColor:"var(--c-bg0)"}}/></div>
                </div>
              </div>
              <div style={{background:"var(--c-bg0)",borderRadius:10,padding:14,marginBottom:6}}>
                <div style={{fontSize:11,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>
                  ✓ {eligiblePlayers.length} player{eligiblePlayers.length!==1?"s":""} will be added automatically
                </div>
                {autoEligiblePlayers.length===0?(
                  <div style={{fontSize:12,color:"var(--c-muted2)"}}>No eligible players found for {gender==="female"?"Girls":"Boys"} {ageCategory}. You can still create the team and add players later.</div>
                ):(
                  <div style={{display:"flex",flexWrap:"wrap",gap:5,maxHeight:140,overflowY:"auto"}}>
                    {eligiblePlayers.map(p=>(
                      <span key={p.id} style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11,padding:"3px 4px 3px 8px",borderRadius:6,background:"var(--c-surface)",color:"var(--c-text3)"}}>
                        <Flag value={p.nationality}/> {p.name}
                        <button type="button" onClick={()=>removeFromEligible(p.id)} title="Remove from this team" style={{display:"flex",alignItems:"center",justifyContent:"center",width:15,height:15,borderRadius:"50%",border:"none",background:"var(--c-border)",color:"var(--c-text2)",fontSize:10,lineHeight:1,cursor:"pointer",padding:0}}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {excludedIds.size>0&&(
                <div style={{background:"var(--c-bg0)",borderRadius:10,padding:14,border:"1px dashed var(--c-border)"}}>
                  <div style={{fontSize:11,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>
                    Removed ({excludedIds.size}) — tap to add back
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                    {autoEligiblePlayers.filter(p=>excludedIds.has(p.id)).map(p=>(
                      <button key={p.id} type="button" onClick={()=>restoreToEligible(p.id)} style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11,padding:"3px 8px",borderRadius:6,background:"none",border:"1px solid var(--c-border)",color:"var(--c-muted)",cursor:"pointer"}}>
                        <span>+</span> <Flag value={p.nationality}/> {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {step===3&&(
          <div style={{padding:"14px 20px",borderTop:"1px solid var(--c-border)",flexShrink:0,paddingBottom:"calc(14px + env(safe-area-inset-bottom, 0px))"}}>
            <button disabled={loading||!teamName.trim()} onClick={async()=>{
              setLoading(true);
              await onCreate({name:teamName,badge,color,gender,ageCategory,cutoffDate,eligiblePlayerIds:eligiblePlayers.map(p=>p.id)});
              setLoading(false);
            }} style={{width:"100%",padding:"12px",borderRadius:9,border:"none",background:color,color:"#fff",fontWeight:700,fontSize:14,cursor:"pointer",opacity:loading?0.7:1}}>
              {loading?"Creating Team…":`Create ${teamName} →`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Training Group modals ───────────────────────────────────────────────────────
// ── NotesModal ───────────────────────────────────────────────────────────────
// Coach-facing overlay wrapping NotesThread — opened from a player's profile or the Inbox.
// ── AwardAchievementModal ───────────────────────────────────────────────────
// Lets a coach manually award any achievement of the given scope ("team" or
// "individual") to the target. Auto-tracked ones can still be awarded manually
// too (e.g. to backfill or correct something), on top of the automatic checks.

export function AwardAchievementModal({scope,targetName,onClose,onAward,ac,AS,isMobile}){
  const options=ACHIEVEMENTS.filter(a=>a.scope===scope);
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,bottom:isMobile?"var(--nav-h,65px)":0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:120,padding:isMobile?0:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:AS.surfaceColor,borderRadius:isMobile?"16px 16px 0 0":14,width:"100%",maxWidth:isMobile?"100%":420,maxHeight:"80vh",border:`1px solid ${AS.borderColor}`,display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 20px 12px",borderBottom:`1px solid ${AS.borderColor}`,flexShrink:0,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Award Achievement</div>
            <div style={{fontSize:15,fontWeight:700}}>{targetName}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:18}}>×</button>
        </div>
        <div style={{padding:12,overflowY:"auto"}}>
          {options.map(a=>(
            <button key={a.key} onClick={()=>onAward(a.key)} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 11px",borderRadius:8,border:"none",background:"var(--c-bg0)",cursor:"pointer",textAlign:"left",marginBottom:6}}>
              <span style={{fontSize:22}}>{a.icon}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:"var(--c-text)"}}>{a.name}</div>
                <div style={{fontSize:10,color:"var(--c-muted)"}}>{a.desc}</div>
              </div>
              <span style={{fontSize:16,color:ac}}>+</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function NotesModal({player,auth,ac,AS,isMobile,onClose,onActivity}){
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,bottom:isMobile?"var(--nav-h,65px)":0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:110,padding:isMobile?0:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:AS.surfaceColor,borderRadius:isMobile?"16px 16px 0 0":14,width:"100%",maxWidth:isMobile?"100%":440,border:`1px solid ${AS.borderColor}`,padding:20,maxHeight:"85vh",display:"flex",flexDirection:"column"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Notes</div>
            <div style={{fontSize:16,fontWeight:700}}>{player.name}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:18}}>×</button>
        </div>
        <NotesThread token={auth.token} orgId={player.orgId} playerId={player.id} playerName={player.name} senderType="coach" senderId={auth.coach.id} ac={ac} AS={AS} isMobile={isMobile} onActivity={onActivity}/>
      </div>
    </div>
  );
}

export function NewGroupModal({allPlayers,onClose,onCreate,ac,isMobile}){
  const [name,setName]=useState("");
  const [badge,setBadge]=useState("🏋️");
  const [selected,setSelected]=useState(()=>new Set());
  const [q,setQ]=useState("");
  const inp={width:"100%",padding:"9px 11px",borderRadius:7,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:13,outline:"none",boxSizing:"border-box"};
  const lbl={display:"block",fontSize:10,fontWeight:600,color:"var(--c-text)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em"};
  function toggle(id){setSelected(prev=>{const next=new Set(prev);next.has(id)?next.delete(id):next.add(id);return next;});}
  const filtered=allPlayers.filter(p=>p.name.toLowerCase().includes(q.toLowerCase()));
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,bottom:isMobile?"var(--nav-h,65px)":0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:100,padding:isMobile?0:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--c-surface)",borderRadius:isMobile?"16px 16px 0 0":14,width:"100%",maxWidth:isMobile?"100%":420,maxHeight:isMobile?"90vh":"85vh",border:"1px solid var(--c-border)",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 20px 12px",borderBottom:"1px solid var(--c-border)",flexShrink:0,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <h2 style={{margin:0,fontSize:16,fontWeight:800}}>New Training Group</h2>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:18}}>×</button>
        </div>
        <div style={{padding:"14px 20px",overflowY:"auto",flex:1}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 60px",gap:9,marginBottom:12}}>
            <div><label style={lbl}>Group Name</label><input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. U15/U18 Combined" style={inp}/></div>
            <div><label style={lbl}>Icon</label><IconPickerSelect value={badge} onChange={setBadge} AS={{borderColor:"var(--c-border)",bgColor:"var(--c-bg0)"}}/></div>
          </div>
          <label style={lbl}>Select Players ({selected.size} selected)</label>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search players…" style={{...inp,marginBottom:8}}/>
          <div style={{maxHeight:260,overflowY:"auto",border:"1px solid var(--c-border)",borderRadius:8}}>
            {filtered.length===0?<div style={{padding:16,textAlign:"center",color:"var(--c-muted2)",fontSize:11}}>No players found.</div>:filtered.map(p=>(
              <label key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderBottom:"1px solid var(--c-border)",cursor:"pointer"}}>
                <input type="checkbox" checked={selected.has(p.id)} onChange={()=>toggle(p.id)}/>
                <span style={{fontSize:12,fontWeight:600,flex:1}}><Flag value={p.nationality}/> {p.name}</span>
                <span style={{fontSize:9,color:"var(--c-muted)"}}>{p.position}</span>
              </label>
            ))}
          </div>
        </div>
        <div style={{padding:"12px 20px",borderTop:"1px solid var(--c-border)",flexShrink:0}}>
          <button onClick={()=>name.trim()&&onCreate(name.trim(),badge,[...selected])} disabled={!name.trim()} style={{width:"100%",padding:"11px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:name.trim()?"pointer":"default",opacity:name.trim()?1:0.5}}>Create Group</button>
        </div>
      </div>
    </div>
  );
}

export function ManageGroupModal({group,allPlayers,onClose,onSave,onRename,onDelete,ac,isMobile}){
  const [name,setName]=useState(group.name);
  const [badge,setBadge]=useState(group.badge||"🏋️");
  const [selected,setSelected]=useState(()=>new Set(group.playerIds));
  const [q,setQ]=useState("");
  const [confirmDel,setConfirmDel]=useState(false);
  const inp={width:"100%",padding:"9px 11px",borderRadius:7,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:13,outline:"none",boxSizing:"border-box"};
  const lbl={display:"block",fontSize:10,fontWeight:600,color:"var(--c-text)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em"};
  function toggle(id){setSelected(prev=>{const next=new Set(prev);next.has(id)?next.delete(id):next.add(id);return next;});}
  const filtered=allPlayers.filter(p=>p.name.toLowerCase().includes(q.toLowerCase()));
  function save(){ if(name.trim()!==group.name||badge!==group.badge)onRename(group.id,name.trim()||group.name,badge); onSave(group.id,[...selected]); onClose(); }
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,bottom:isMobile?"var(--nav-h,65px)":0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:100,padding:isMobile?0:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--c-surface)",borderRadius:isMobile?"16px 16px 0 0":14,width:"100%",maxWidth:isMobile?"100%":420,maxHeight:isMobile?"90vh":"85vh",border:"1px solid var(--c-border)",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 20px 12px",borderBottom:"1px solid var(--c-border)",flexShrink:0,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <h2 style={{margin:0,fontSize:16,fontWeight:800}}>Manage Group</h2>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:18}}>×</button>
        </div>
        <div style={{padding:"14px 20px",overflowY:"auto",flex:1}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 60px",gap:9,marginBottom:12}}>
            <div><label style={lbl}>Group Name</label><input value={name} onChange={e=>setName(e.target.value)} style={inp}/></div>
            <div><label style={lbl}>Icon</label><IconPickerSelect value={badge} onChange={setBadge} AS={{borderColor:"var(--c-border)",bgColor:"var(--c-bg0)"}}/></div>
          </div>
          <label style={lbl}>Players ({selected.size} selected)</label>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search players…" style={{...inp,marginBottom:8}}/>
          <div style={{maxHeight:220,overflowY:"auto",border:"1px solid var(--c-border)",borderRadius:8,marginBottom:14}}>
            {filtered.length===0?<div style={{padding:16,textAlign:"center",color:"var(--c-muted2)",fontSize:11}}>No players found.</div>:filtered.map(p=>(
              <label key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderBottom:"1px solid var(--c-border)",cursor:"pointer"}}>
                <input type="checkbox" checked={selected.has(p.id)} onChange={()=>toggle(p.id)}/>
                <span style={{fontSize:12,fontWeight:600,flex:1}}><Flag value={p.nationality}/> {p.name}</span>
                <span style={{fontSize:9,color:"var(--c-muted)"}}>{p.position}</span>
              </label>
            ))}
          </div>
          <div style={{borderTop:"1px solid #7f1d1d55",paddingTop:12}}>
            <div style={{fontSize:10,fontWeight:700,color:"#ef4444",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Danger Zone</div>
            {!confirmDel?(
              <button onClick={()=>setConfirmDel(true)} style={{padding:"7px 14px",borderRadius:6,border:"1px solid #ef4444",background:"none",color:"#ef4444",fontWeight:700,fontSize:11,cursor:"pointer"}}>Delete This Group</button>
            ):(
              <div style={{background:"var(--c-bg0)",borderRadius:8,padding:10,border:"1px solid #7f1d1d"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#fca5a5",marginBottom:8}}>Delete "{group.name}" and all its sessions? This can't be undone.</div>
                <div style={{display:"flex",gap:7}}>
                  <button onClick={()=>onDelete(group.id)} style={{flex:1,padding:"7px",borderRadius:6,border:"none",background:"#ef4444",color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>Yes, Delete</button>
                  <button onClick={()=>setConfirmDel(false)} style={{padding:"7px 12px",borderRadius:6,border:"1px solid var(--c-border)",background:"none",color:"var(--c-text2)",fontSize:11,cursor:"pointer"}}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{padding:"12px 20px",borderTop:"1px solid var(--c-border)",flexShrink:0}}>
          <button onClick={save} style={{width:"100%",padding:"11px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer"}}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

export function SquadView({players,team,token,coachId,onLineupsChanged,onStrategiesChanged,ac,AS,isMobile}){
  const [ptab,setPtab]=useState("team"); // "team" | "strategy"

  // ── Team tab (named starting-team lineups) ──
  const [sc,setSc]=useState(11);
  const [sl,setSl]=useState(()=>mkSlots(11));
  const [subs,setSubs]=useState([]);
  const [lineups,setLineups]=useState(null);
  const [editingLineupId,setEditingLineupId]=useState(null);
  const [lineupName,setLineupName]=useState("");

  useEffect(()=>{ if(team)loadLineups(); },[team?.id]);
  async function loadLineups(){
    const rows=await api.get(token,"team_lineups",`team_id=eq.${team.id}&order=created_at.asc`);
    setLineups(rows||[]);
    if(onLineupsChanged)onLineupsChanged(team.id,rows||[]);
  }
  function resetLineupBoard(){
    setSc(11);setSl(mkSlots(11));setSubs([]);setEditingLineupId(null);setLineupName("");
  }
  function openLineup(l){
    setSc(l.side_count||11);setSl(l.slots||mkSlots(l.side_count||11));setSubs(l.subs||[]);setEditingLineupId(l.id);setLineupName(l.name);
  }
  async function saveLineup(){
    const trimmed=lineupName.trim();
    if(!trimmed){alert("Please give this starting team a name.");return;}
    if(editingLineupId){
      const res=await api.patch(token,"team_lineups",editingLineupId,{name:trimmed,side_count:sc,slots:sl,subs,updated_at:new Date().toISOString()});
      if(res&&res.length>0){const next=(lineups||[]).map(l=>l.id===editingLineupId?res[0]:l);setLineups(next);if(onLineupsChanged)onLineupsChanged(team.id,next);}
    } else {
      const created=await api.post(token,"team_lineups",[{team_id:team.id,name:trimmed,side_count:sc,slots:sl,subs,created_by:coachId}]);
      if(created&&created[0]){const next=[...(lineups||[]),created[0]];setLineups(next);setEditingLineupId(created[0].id);if(onLineupsChanged)onLineupsChanged(team.id,next);}
    }
  }
  async function deleteLineup(id){
    await api.del(token,"team_lineups",id);
    const next=(lineups||[]).filter(l=>l.id!==id);
    setLineups(next);
    if(onLineupsChanged)onLineupsChanged(team.id,next);
    if(editingLineupId===id)resetLineupBoard();
  }

  // ── Strategy tab (tactical drawing board + saved strategies) ──
  const [stratSc,setStratSc]=useState(11);
  const [stratSl,setStratSl]=useState(()=>mkSlots(11));
  const [stratSubs,setStratSubs]=useState([]);
  const [drawings,setDrawings]=useState([]);
  const [tool,setTool]=useState("move");
  const [color,setColor]=useState("#ef4444");
  const [strategies,setStrategies]=useState(null);
  const [editingId,setEditingId]=useState(null);
  const [nameInput,setNameInput]=useState("");
  const [notesInput,setNotesInput]=useState("");
  const [saveCategory,setSaveCategory]=useState("general");
  const [openCategories,setOpenCategories]=useState(["attack","defence","general","setpieces"]);

  useEffect(()=>{ if(team)loadStrategies(); },[team?.id]);
  async function loadStrategies(){
    const rows=await api.get(token,"team_strategies",`team_id=eq.${team.id}&order=created_at.asc`);
    setStrategies(rows||[]);
    if(onStrategiesChanged)onStrategiesChanged(team.id,rows||[]);
  }
  function resetStrategyBoard(){
    setStratSc(11);setStratSl(mkSlots(11));setStratSubs([]);setDrawings([]);setEditingId(null);setNameInput("");setNotesInput("");setSaveCategory("general");
  }
  function openStrategy(s){
    setStratSc(s.side_count||11);setStratSl(s.slots||mkSlots(s.side_count||11));setStratSubs(s.subs||[]);setDrawings(s.drawings||[]);setEditingId(s.id);setNameInput(s.name);setNotesInput(s.notes||"");setSaveCategory(s.category);setTool("move");
  }
  async function saveStrategy(){
    const trimmed=nameInput.trim();
    if(!trimmed){alert("Please give this strategy a name.");return;}
    if(editingId){
      const res=await api.patch(token,"team_strategies",editingId,{category:saveCategory,name:trimmed,notes:notesInput,side_count:stratSc,slots:stratSl,subs:stratSubs,drawings,updated_at:new Date().toISOString()});
      if(res&&res.length>0){const next=(strategies||[]).map(s=>s.id===editingId?res[0]:s);setStrategies(next);if(onStrategiesChanged)onStrategiesChanged(team.id,next);}
    } else {
      const created=await api.post(token,"team_strategies",[{team_id:team.id,category:saveCategory,name:trimmed,notes:notesInput,side_count:stratSc,slots:stratSl,subs:stratSubs,drawings,created_by:coachId}]);
      if(created&&created[0]){const next=[...(strategies||[]),created[0]];setStrategies(next);setEditingId(created[0].id);if(onStrategiesChanged)onStrategiesChanged(team.id,next);}
    }
  }
  async function deleteStrategy(id){
    await api.del(token,"team_strategies",id);
    const next=(strategies||[]).filter(s=>s.id!==id);
    setStrategies(next);
    if(onStrategiesChanged)onStrategiesChanged(team.id,next);
    if(editingId===id)resetStrategyBoard();
  }

  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  if(players.length===0)return <div style={{...C,padding:36,textAlign:"center",color:"var(--c-muted2)",fontSize:11}}>Add players in Roster first.</div>;

  return(<div style={{...C,padding:16}}>
    <div style={{display:"flex",gap:3,background:AS.bgColor,borderRadius:8,padding:3,marginBottom:14}}>
      {[["team","Team"],["strategy","🎯 Strategy"]].map(([v,l])=>(
        <button key={v} onClick={()=>setPtab(v)} style={{flex:1,padding:"8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,background:ptab===v?ac:"transparent",color:ptab===v?"#fff":"var(--c-muted)"}}>{l}</button>
      ))}
    </div>

    {ptab==="team"&&(()=>{
      const pitchEl=<Pitch players={players} slots={sl} setSlots={setSl} tc={ac} sideCount={sc} setSideCount={setSc} subs={subs} setSubs={setSubs}/>;
      const sidePanel=(
        <>
          <div style={{padding:14,background:AS.bgColor,borderRadius:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>{editingLineupId?"Update Starting Team":"Save Starting Team"}</div>
            <input value={lineupName} onChange={e=>setLineupName(e.target.value)} placeholder="Starting team name…" style={{width:"100%",padding:"8px 10px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.surfaceColor,color:"var(--c-text)",fontSize:12,marginBottom:10,boxSizing:"border-box"}}/>
            <div style={{display:"flex",gap:8}}>
              <button onClick={saveLineup} style={{flex:1,padding:"9px",borderRadius:7,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>{editingLineupId?"Update":"💾 Save"}</button>
              <button onClick={resetLineupBoard} style={{padding:"9px 14px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:12,cursor:"pointer"}}>New</button>
            </div>
          </div>
          <div style={{marginTop:16}}>
            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>🏁 Starting Teams ({(lineups||[]).length})</div>
            {lineups===null?(
              <div style={{fontSize:11,color:"var(--c-muted2)"}}>Loading…</div>
            ):lineups.length===0?(
              <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>No starting teams saved yet.</div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {lineups.map(l=>(
                  <div key={l.id} style={{display:"flex",alignItems:"center",gap:8,background:l.id===editingLineupId?ac+"22":AS.bgColor,border:l.id===editingLineupId?`1px solid ${ac}`:"1px solid transparent",borderRadius:8,padding:"9px 11px"}}>
                    <span style={{flex:1,fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</span>
                    <button onClick={()=>openLineup(l)} style={{fontSize:10,fontWeight:700,color:ac,background:"none",border:"none",cursor:"pointer"}}>Open</button>
                    <button onClick={()=>deleteLineup(l.id)} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:13,padding:0}}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      );
      if(isMobile){
        return(<>{pitchEl}<div style={{marginTop:16}}>{sidePanel}</div></>);
      }
      return(
        <div style={{display:"flex",gap:20,alignItems:"flex-start"}}>
          <div style={{flex:1,minWidth:0}}>{pitchEl}</div>
          <div style={{width:260,flexShrink:0}}>{sidePanel}</div>
        </div>
      );
    })()}

    {ptab==="strategy"&&(()=>{
      const categories=[["attack","⚔️ Attack"],["defence","🛡️ Defence"],["general","📋 General"],["setpieces","🥅 Set Pieces"]];
      const boardEl=(
        <>
          {lineups&&lineups.length>0&&(
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
              <span style={{fontSize:11,color:"var(--c-muted)",fontWeight:600}}>📥 Load Starting Team:</span>
              <select value="" onChange={e=>{const l=lineups.find(x=>x.id===e.target.value);if(l){setStratSc(l.side_count||11);setStratSl(l.slots||mkSlots(l.side_count||11));setStratSubs(l.subs||[]);}}} style={{padding:"6px 9px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text2)",fontSize:11,cursor:"pointer"}}>
                <option value="">Choose…</option>
                {lineups.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}
          <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>Format</div>
          <PitchFormatRow slots={stratSl} setSlots={setStratSl} sideCount={stratSc} setSideCount={setStratSc} tc={ac}/>
          <div style={{borderTop:`1px solid ${AS.borderColor}`,margin:"10px 0"}}/>
          <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>Tools</div>
          <DrawingToolbar tool={tool} setTool={setTool} color={color} setColor={setColor} onClear={()=>setDrawings([])} hasDrawings={drawings.length>0} ac={ac}/>
          <div style={{borderTop:`1px solid ${AS.borderColor}`,margin:"0 0 10px"}}/>
          <Pitch players={players} slots={stratSl} setSlots={setStratSl} tc={ac} sideCount={stratSc} setSideCount={setStratSc} subs={stratSubs} setSubs={setStratSubs} tool={tool} color={color} drawings={drawings} setDrawings={setDrawings} hideFormatRow/>
        </>
      );
      const sidePanel=(
        <>
          <div style={{padding:14,background:AS.bgColor,borderRadius:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>{editingId?"Update Strategy":"Save New Strategy"}</div>
            <input value={nameInput} onChange={e=>setNameInput(e.target.value)} placeholder="Strategy name…" style={{width:"100%",padding:"8px 10px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.surfaceColor,color:"var(--c-text)",fontSize:12,marginBottom:8,boxSizing:"border-box"}}/>
            <textarea value={notesInput} onChange={e=>setNotesInput(e.target.value)} placeholder="Notes (optional) — a short summary of this strategy…" rows={2} style={{width:"100%",padding:"8px 10px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.surfaceColor,color:"var(--c-text)",fontSize:12,marginBottom:8,boxSizing:"border-box",resize:"vertical",fontFamily:"inherit"}}/>
            <div style={{display:"flex",gap:5,marginBottom:10,flexWrap:"wrap"}}>
              {categories.map(([cat,label])=>(
                <button key={cat} onClick={()=>setSaveCategory(cat)} style={{flex:"1 1 45%",padding:"7px 4px",borderRadius:6,border:"none",cursor:"pointer",fontSize:10,fontWeight:700,background:saveCategory===cat?ac:AS.surfaceColor,color:saveCategory===cat?"#fff":"var(--c-muted)"}}>{label}</button>
              ))}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={saveStrategy} style={{flex:1,padding:"9px",borderRadius:7,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>{editingId?"Update":"💾 Save"}</button>
              <button onClick={resetStrategyBoard} style={{padding:"9px 14px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:12,cursor:"pointer"}}>New</button>
            </div>
          </div>
          <div style={{marginTop:16,display:"flex",flexDirection:"column",gap:10}}>
            {categories.map(([cat,label])=>{
              const list=(strategies||[]).filter(s=>s.category===cat);
              const isOpen=openCategories.includes(cat);
              return(
                <div key={cat}>
                  <button onClick={()=>setOpenCategories(prev=>prev.includes(cat)?prev.filter(c=>c!==cat):[...prev,cat])} style={{width:"100%",display:"flex",alignItems:"center",gap:6,background:"none",border:"none",padding:0,cursor:"pointer",marginBottom:isOpen?8:0}}>
                    <span style={{fontSize:9,color:"var(--c-muted)",transform:isOpen?"rotate(90deg)":"none",transition:"transform 0.15s",flexShrink:0}}>▶</span>
                    <span style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",flex:1,textAlign:"left"}}>{label} ({list.length}/5)</span>
                  </button>
                  {isOpen&&(
                    strategies===null?(
                      <div style={{fontSize:11,color:"var(--c-muted2)"}}>Loading…</div>
                    ):list.length===0?(
                      <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>No strategies saved yet.</div>
                    ):(
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {list.map(s=>(
                          <div key={s.id} style={{background:s.id===editingId?ac+"22":AS.bgColor,border:s.id===editingId?`1px solid ${ac}`:"1px solid transparent",borderRadius:8,padding:"9px 11px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <span style={{flex:1,fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</span>
                              <button onClick={()=>openStrategy(s)} style={{fontSize:10,fontWeight:700,color:ac,background:"none",border:"none",cursor:"pointer"}}>Open</button>
                              <button onClick={()=>deleteStrategy(s.id)} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:13,padding:0}}>✕</button>
                            </div>
                            {s.notes&&<div style={{fontSize:10,color:"var(--c-muted)",marginTop:5,lineHeight:1.4}}>{s.notes}</div>}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </>
      );
      if(isMobile){
        return(<>{boardEl}<div style={{marginTop:16}}>{sidePanel}</div></>);
      }
      return(
        <div style={{display:"flex",gap:20,alignItems:"flex-start"}}>
          <div style={{flex:1,minWidth:0}}>{boardEl}</div>
          <div style={{width:260,flexShrink:0}}>{sidePanel}</div>
        </div>
      );
    })()}
  </div>);
}

// ── MainApp ───────────────────────────────────────────────────────────────────
// ── AttRegister ───────────────────────────────────────────────────────────────
// Take/edit attendance for one session: tap a player to cycle through statuses.

export function AttRegister({sessions, aSess, reg, setReg, teamPlayers, ac, AS, C, onBack, onSave, readOnly}){
  const session = sessions.find(s=>s.id===aSess);
  const statusOrder = ["present","late","excused","absent"];

  function cycleStatus(playerId){
    if(readOnly)return;
    setReg(prev=>{
      const cur = prev[playerId] || "present";
      const next = statusOrder[(statusOrder.indexOf(cur)+1) % statusOrder.length];
      return {...prev, [playerId]: next};
    });
  }

  if(!session) return <div style={{...C,padding:30,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>Session not found.</div>;

  const counts = {present:0,late:0,excused:0,absent:0};
  teamPlayers.forEach(p=>{ const s=reg[p.id]||"present"; if(counts[s]!==undefined)counts[s]++; });

  return(
    <div>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
        <button onClick={onBack} style={{background:"none",border:`1px solid ${AS.borderColor}`,color:"var(--c-muted)",cursor:"pointer",fontSize:11,padding:"6px 10px",borderRadius:6}}>← Back</button>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:14}}>{session.label}</div>
          <div style={{fontSize:11,color:"var(--c-muted)",marginTop:1}}>{session.type==="game"?"⚽ Match":"🏃 Training"} · {fmtDate(session.date)}</div>
        </div>
      </div>

      {/* Summary counts */}
      <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
        {Object.entries(counts).map(([key,count])=>(
          <span key={key} style={{fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:6,background:ATT[key].bg,color:ATT[key].text}}>{count} {ATT[key].label}</span>
        ))}
      </div>

      {readOnly&&(
        <div style={{...C,padding:"9px 12px",marginBottom:12,fontSize:11,color:"var(--c-text2)",display:"flex",alignItems:"center",gap:7}}>
          <span style={{fontSize:14}}>👁️</span> View-only — you can't edit this register.
        </div>
      )}

      {/* Player rows */}
      <div style={{display:"flex",flexDirection:"column",gap:5}}>
        {teamPlayers.length===0?(
          <div style={{...C,padding:24,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No players on this team yet.</div>
        ):teamPlayers.map(p=>{
          const status = reg[p.id] || "present";
          const sc = ATT[status];
          return(
            <button key={p.id} onClick={()=>cycleStatus(p.id)} disabled={readOnly} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:9,
              border:`1px solid ${sc.color}44`,background:AS.surfaceColor,cursor:readOnly?"default":"pointer",textAlign:"left",opacity:readOnly?0.8:1}}>
              <span style={{width:30,height:30,borderRadius:"50%",background:sc.color+"22",border:`2px solid ${sc.color}`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:11,color:sc.color,flexShrink:0}}>{p.number||"–"}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:12,color:"var(--c-text)"}}><Flag value={p.nationality}/> {p.name}</div>
                <div style={{fontSize:10,color:"var(--c-muted)"}}>{p.position}</div>
              </div>
              <span style={{fontSize:10,fontWeight:700,padding:"4px 10px",borderRadius:6,background:sc.bg,color:sc.text}}>{sc.label}</span>
            </button>
          );
        })}
      </div>

      {!readOnly&&(
        <button onClick={onSave} style={{width:"100%",marginTop:14,padding:"12px",borderRadius:9,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:14,cursor:"pointer"}}>
          Save Register ✓
        </button>
      )}
    </div>
  );
}

// ── Feature toggles ──────────────────────────────────────────────────────────
// Per-org on/off switches a head coach controls — when a feature is off it's
// hidden from every view (coaches, players, parents) but nothing is deleted,
// so switching it back on brings everything right back.

